import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, Phone, RefreshCw, Package, Truck, Printer,
  Save, Trash2, Search, Eye, Pencil, MapPin, Box,
  Loader2, CheckCircle2, X, ChevronUp, ChevronDown, Globe, Tag,
} from 'lucide-react';
import { adminAPI, websiteSettingsAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import './OrderManagement.css';
import '../../components/admin/MobileCardList.css';
import {
  exportMohammadParcelXLSX,
  exportProductDealerXLSX,
  exportStandardXLSX,
  exportStandardCSV,
} from '../../utils/orderExport';

const STATUS_OPTIONS = ['preorder', 'pending', 'no_response', 'processing', 'shipped', 'in_querier', 'partially_delivered', 'delivered', 'cancelled'] as const;
type StatusFilter = 'all' | 'new' | 'incomplete' | 'future' | 'not_response' | 'confirmed' | 'confirmed_sent' | 'in_querier' | 'partially_delivered' | 'delivered' | 'cancelled'
                  | 'preorder' | 'pending' | 'no_response' | 'processing' | 'shipped';

// Which individual statuses belong to each group
const STATUS_GROUPS: Record<string, string[]> = {
  new:                 ['pending'],
  incomplete:          ['preorder'],
  future:              ['preorder'],
  not_response:        ['no_response'],
  confirmed:           ['processing'],
  in_querier:          ['shipped', 'in_querier'],
  partially_delivered: ['partially_delivered'],
  delivered:           ['delivered'],
  cancelled:           ['cancelled'],
};

const EDIT_STATUS_OPTIONS = [
  { value: 'pending',             label: 'New (Pending)',             icon: '🆕', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { value: 'preorder',            label: 'Incomplete (PreOrder)',     icon: '⏳', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  { value: 'no_response',         label: 'No Response',               icon: '📞', color: '#d97706', bg: '#fef9ee', border: '#fde68a' },
  { value: 'processing',          label: 'Confirmed (Processing)',    icon: '✅', color: '#0d9488', bg: '#f0fdfb', border: '#99f6e4' },
  { value: 'shipped',             label: 'Shipped',                   icon: '🚚', color: '#0284c7', bg: '#e0f2fe', border: '#7dd3fc' },
  { value: 'in_querier',          label: 'In Querier',                icon: '🛵', color: '#0284c7', bg: '#e0f2fe', border: '#7dd3fc' },
  { value: 'partially_delivered', label: 'Partially Delivered',       icon: '📦', color: '#ea580c', bg: '#ffedd5', border: '#fed7aa' },
  { value: 'delivered',           label: 'Delivered',                 icon: '🎉', color: '#16a34a', bg: '#dcfce7', border: '#86efac' },
];

const TRAFFIC_PLATFORM_OPTIONS = [
  { value: 'manual',    label: 'Manual (Phone / Call)', icon: '📞', color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
  { value: 'facebook',  label: 'Facebook (Page / Ads)', icon: '🟦', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  { value: 'tiktok',    label: 'TikTok (Ads / Page)',   icon: '🎵', color: '#be185d', bg: '#fdf2f8', border: '#fbcfe8' },
  { value: 'whatsapp',  label: 'WhatsApp',              icon: '💬', color: '#15803d', bg: '#dcfce7', border: '#86efac' },
  { value: 'google',    label: 'Google Ads / Search',   icon: '🔍', color: '#a16207', bg: '#fefce8', border: '#fde047' },
  { value: 'organic',   label: 'Direct / Website',      icon: '🌐', color: '#475569', bg: '#f1f5f9', border: '#cbd5e1' },
];

const convertBanglaToEnglishDigits = (str: string): string => {
  const banglaDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return String(str || '').replace(/[০-৯]/g, (d) => String(banglaDigits.indexOf(d)));
};

const matchesPhone = (orderPhone: string | undefined | null, search: string): boolean => {
  if (!orderPhone || !search) return false;
  const rawOrder = convertBanglaToEnglishDigits(String(orderPhone)).trim().toLowerCase();
  const rawSearch = convertBanglaToEnglishDigits(String(search)).trim().toLowerCase();
  if (rawOrder.includes(rawSearch)) return true;

  const orderDigits = rawOrder.replace(/\D/g, '');
  const searchDigits = rawSearch.replace(/\D/g, '');

  if (searchDigits.length >= 3) {
    if (orderDigits.includes(searchDigits)) return true;
    if (searchDigits.includes(orderDigits) && orderDigits.length >= 7) return true;

    // Normalize BD country codes (880 or 88)
    const normOrder = orderDigits.replace(/^(880|88)/, '0');
    const normSearch = searchDigits.replace(/^(880|88)/, '0');

    if (normOrder.includes(normSearch)) return true;

    // Compare without leading zeros (e.g. searching 1711223344 for 01711223344)
    const orderNoZero = normOrder.replace(/^0+/, '');
    const searchNoZero = normSearch.replace(/^0+/, '');

    if (orderNoZero.includes(searchNoZero)) return true;
  }
  return false;
};

const matchesOrderSearch = (order: any, search: string): boolean => {
  if (!search) return true;
  const t = convertBanglaToEnglishDigits(search).trim().toLowerCase();
  if (!t) return true;

  // 1. Customer phone (smart normalization)
  if (matchesPhone(order.customer_phone, t)) return true;

  // 2. Order number
  if (order.order_number?.toLowerCase().includes(t)) return true;

  // 3. Customer name
  if (order.customer_name?.toLowerCase().includes(t)) return true;

  // 4. Steadfast courier tracking & consignment
  if (order.steadfast_tracking_code?.toLowerCase().includes(t)) return true;
  if (order.steadfast_consignment_id?.toLowerCase().includes(t)) return true;

  // 5. Shipping address
  if (order.shipping_address?.toLowerCase().includes(t)) return true;

  // 6. Notes
  if (order.notes?.toLowerCase().includes(t)) return true;

  // 7. Ordered products
  if (Array.isArray(order.items) && order.items.some((it: any) =>
    (it.product_name || it.name)?.toLowerCase().includes(t)
  )) {
    return true;
  }

  return false;
};

const STATUS_FILTER_LABELS: Record<string, string> = {
  all: 'All',
  new: 'New Orders',
  confirmed: 'Confirmed',
  in_querier: 'In Querier',
  confirmed_sent: 'Ready To Delivery',
  partially_delivered: 'Partially Delivered',
  delivered: 'Delivered',
  not_response: 'Not Response',
  cancelled: 'Cancelled',
  incomplete: 'Incomplete',
};

const renderHighlightedPhone = (phone: string | undefined | null, search: string): React.ReactNode => {
  if (!phone) return '—';
  if (!search || !search.trim()) return phone;

  const rawPhone = String(phone);
  const normalizedSearch = convertBanglaToEnglishDigits(search).trim();
  const searchDigits = normalizedSearch.replace(/\D/g, '');

  // 1. Direct case-insensitive literal substring match
  const lowerPhone = rawPhone.toLowerCase();
  const lowerSearch = normalizedSearch.toLowerCase();
  const directIdx = lowerPhone.indexOf(lowerSearch);
  if (directIdx !== -1 && lowerSearch.length > 0) {
    const before = rawPhone.slice(0, directIdx);
    const match = rawPhone.slice(directIdx, directIdx + lowerSearch.length);
    const after = rawPhone.slice(directIdx + lowerSearch.length);
    return (
      <>
        {before}
        <mark className="om-phone-highlight">{match}</mark>
        {after}
      </>
    );
  }

  // 2. If search has digits, check for digit-level match in phone
  if (searchDigits.length >= 3) {
    const digitIdx = rawPhone.indexOf(searchDigits);
    if (digitIdx !== -1) {
      const before = rawPhone.slice(0, digitIdx);
      const match = rawPhone.slice(digitIdx, digitIdx + searchDigits.length);
      const after = rawPhone.slice(digitIdx + searchDigits.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const searchNo88 = searchDigits.replace(/^(880|88)/, '0');
    if (searchNo88.length >= 3 && rawPhone.indexOf(searchNo88) !== -1) {
      const idx = rawPhone.indexOf(searchNo88);
      const before = rawPhone.slice(0, idx);
      const match = rawPhone.slice(idx, idx + searchNo88.length);
      const after = rawPhone.slice(idx + searchNo88.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const searchNoZero = searchDigits.replace(/^(880|88|0)/, '');
    if (searchNoZero.length >= 3 && rawPhone.indexOf(searchNoZero) !== -1) {
      const idx = rawPhone.indexOf(searchNoZero);
      const before = rawPhone.slice(0, idx);
      const match = rawPhone.slice(idx, idx + searchNoZero.length);
      const after = rawPhone.slice(idx + searchNoZero.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const phoneDigitsMap: number[] = [];
    let cleanPhoneDigits = '';
    for (let i = 0; i < rawPhone.length; i++) {
      if (/\d/.test(rawPhone[i])) {
        cleanPhoneDigits += rawPhone[i];
        phoneDigitsMap.push(i);
      }
    }

    const matchTargets = [searchDigits, searchNo88, searchNoZero].filter(s => s && s.length >= 3);
    for (const target of matchTargets) {
      const dIdx = cleanPhoneDigits.indexOf(target);
      if (dIdx !== -1 && dIdx + target.length <= phoneDigitsMap.length) {
        const startCharIdx = phoneDigitsMap[dIdx];
        const endCharIdx = phoneDigitsMap[dIdx + target.length - 1] + 1;
        const before = rawPhone.slice(0, startCharIdx);
        const match = rawPhone.slice(startCharIdx, endCharIdx);
        const after = rawPhone.slice(endCharIdx);
        return (
          <>
            {before}
            <mark className="om-phone-highlight">{match}</mark>
            {after}
          </>
        );
      }
    }
  }

  return rawPhone;
};

const renderHighlightedText = (text: string | undefined | null, search: string): React.ReactNode => {
  if (!text) return '—';
  if (!search || !search.trim()) return text;
  const rawText = String(text);
  const lowerText = rawText.toLowerCase();
  const lowerSearch = search.trim().toLowerCase();
  const idx = lowerText.indexOf(lowerSearch);
  if (idx === -1) return rawText;
  const before = rawText.slice(0, idx);
  const match = rawText.slice(idx, idx + lowerSearch.length);
  const after = rawText.slice(idx + lowerSearch.length);
  return (
    <>
      {before}
      <mark className="om-text-highlight">{match}</mark>
      {after}
    </>
  );
};

const OrderManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [trafficSourceFilter, setTrafficSourceFilter] = useState<string>('all');
  const [sendingToSteadfast, setSendingToSteadfast] = useState<number | null>(null);

  // Bulk Send to Steadfast (create consignments)
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendResult, setBulkSendResult] = useState<{ sent: number; failed: number; already_sent: number; results: any[] } | null>(null);

  // Pickup Request (physical pickup / manifest)
  const [pickupRequesting, setPickupRequesting] = useState(false);
  const [pickupResult, setPickupResult] = useState<{ requested: number; already_requested: number; total_cod: number; pickup_time: string; results: any[]; pickup_api_success?: boolean; pickup_api_error?: string; pickup_id?: number } | null>(null);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Bulk Category Assignment
  const [assignCategory, setAssignCategory] = useState<string>('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Per-row inline category update
  const [categoryUpdating, setCategoryUpdating] = useState<Set<number>>(new Set());

  // Print Labels
  const [printingLabel, setPrintingLabel] = useState<number | null>(null);
  const [bulkPrinting, setBulkPrinting] = useState(false);

  // Steadfast Status Sync
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const [syncStats, setSyncStats] = useState<{ synced: number; changed: number } | null>(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  // Edit modal state
  const [deliveryOptions, setDeliveryOptions] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalTab, setEditModalTab] = useState<'items' | 'slip' | 'fraud' | 'edit'>('items');
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [editItems, setEditItems] = useState<any[]>([]);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProductResults, setEditProductResults] = useState<any[]>([]);
  const [editProductSearching, setEditProductSearching] = useState(false);
  const [editPickingProduct, setEditPickingProduct] = useState<any>(null);
  const [editPickAttrs, setEditPickAttrs] = useState<{ color: string; size: string; weight: string; qty: number; price: number }>({
    color: '', size: '', weight: '', qty: 1, price: 0
  });
  const editSearchRef = useRef<any>(null);
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    status: 'pending',
    shipping_cost: '60',
    delivery_option: null as number | null,
    traffic_source: 'manual',
    utm_campaign: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string>('');

  // Admin discount state
  const [discountType, setDiscountType] = useState<'flat' | 'percentage' | ''>('');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountError, setDiscountError] = useState<string>('');

  // Customer Steadfast History
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);
  const [customerHistoryError, setCustomerHistoryError] = useState<string>('');

  const [siteSettings, setSiteSettings] = useState<{ site_name: string; support_phone: string; logo_url?: string }>({ site_name: '', support_phone: '' });
  const packingSlipRef = useRef<HTMLDivElement>(null);

  // Export Dropdown State
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setExportDropdownOpen(false);
      }
    };
    if (exportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportDropdownOpen]);

  // Date range filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateFilterBasis, setDateFilterBasis] = useState<'auto' | 'status_date' | 'created_at'>('auto');

  // Helper to resolve the relevant date of an order based on current filter & date basis
  const getOrderFilterDate = (order: any, filter: StatusFilter, basis: 'auto' | 'created_at' | 'status_date'): Date | null => {
    if (!order) return null;
    if (basis === 'created_at') {
      return order.created_at ? new Date(order.created_at) : null;
    }
    if (basis === 'status_date') {
      const d = order.status_changed_at || order.confirmed_at || order.shipped_at || order.delivered_at || order.created_at;
      return d ? new Date(d) : null;
    }
    // 'auto' mode: Smart contextual date according to the active category/status filter
    if (filter === 'confirmed') {
      const d = order.confirmed_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'in_querier' || filter === 'confirmed_sent') {
      const d = order.shipped_at || order.steadfast_sent_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'delivered') {
      const d = order.delivered_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'cancelled') {
      const d = order.cancelled_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'partially_delivered') {
      const d = order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    return order.created_at ? new Date(order.created_at) : null;
  };

  // Table: sort & pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Manual Order creation
  // Manual Order creation
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualModalTab, setManualModalTab] = useState<'items' | 'slip' | 'fraud' | 'form'>('items');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState<string>('');
  const [manualSuccess, setManualSuccess] = useState<string>('');
  const [manualProductSearch, setManualProductSearch] = useState<string>('');
  const [manualProductResults, setManualProductResults] = useState<any[]>([]);
  const [manualProductSearching, setManualProductSearching] = useState(false);
  const [manualCartItems, setManualCartItems] = useState<any[]>([]);
  const [manualPickingProduct, setManualPickingProduct] = useState<any>(null);
  const [manualPickAttrs, setManualPickAttrs] = useState<{ color: string; size: string; weight: string; qty: number; price: number }>({ color: '', size: '', weight: '', qty: 1, price: 0 });
  const [manualForm, setManualForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    payment_method: 'cash_on_delivery',
    delivery_option: null as number | null,
    shipping_cost: '60',
    initial_status: 'pending',
    admin_discount_type: null as 'flat' | 'percentage' | null,
    admin_discount_value: '',
    coupon_code: '',
    traffic_source: 'manual',
    utm_campaign: '',
    send_email: false,
  });
  const [manualCouponApplied, setManualCouponApplied] = useState<{ code: string; discount: number } | null>(null);
  const [manualCouponChecking, setManualCouponChecking] = useState(false);
  const [manualCouponError, setManualCouponError] = useState<string>('');
  const manualSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualPackingSlipRef = useRef<HTMLDivElement>(null);

  const manualItemsSubtotal = manualCartItems.reduce(
    (sum, it) => sum + (parseFloat(it.price || 0) * parseInt(it.quantity || it.qty || 1)),
    0
  );
  const manualShipping = parseFloat(manualForm.shipping_cost) || 0;
  const manualCouponDiscount = manualCouponApplied?.discount || 0;
  let manualAdminDiscount = 0;
  if (manualForm.admin_discount_type === 'flat' && manualForm.admin_discount_value) {
    manualAdminDiscount = Math.min(parseFloat(manualForm.admin_discount_value) || 0, manualItemsSubtotal);
  } else if (manualForm.admin_discount_type === 'percentage' && manualForm.admin_discount_value) {
    manualAdminDiscount = (manualItemsSubtotal * (parseFloat(manualForm.admin_discount_value) || 0)) / 100;
  }
  const manualTotal = Math.max(0, manualItemsSubtotal + manualShipping - manualCouponDiscount - manualAdminDiscount);

  const handleOpenManualModal = () => {
    const defaultOpt = deliveryOptions[0];
    setManualForm({
      customer_name: '',
      customer_phone: '',
      shipping_address: '',
      notes: '',
      payment_method: 'cash_on_delivery',
      delivery_option: defaultOpt ? defaultOpt.id : null,
      shipping_cost: defaultOpt ? String(parseFloat(defaultOpt.cost).toFixed(0)) : '60',
      initial_status: 'pending',
      admin_discount_type: null,
      admin_discount_value: '',
      coupon_code: '',
      traffic_source: 'manual',
      utm_campaign: '',
      send_email: false,
    });
    setManualCartItems([]);
    setManualPickingProduct(null);
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setManualProductSearch('');
    setManualProductResults([]);
    setManualCouponApplied(null);
    setManualCouponError('');
    setManualError('');
    setManualSuccess('');
    setManualModalTab('items');
    setCustomerHistory(null);
    setManualModalOpen(true);
  };

  const handleManualPhoneChange = (val: string) => {
    setManualForm(prev => ({ ...prev, customer_phone: val }));
    const clean = val.replace(/[^0-9]/g, '');
    if (clean.length === 11) {
      loadCustomerHistory(clean);
    }
  };

  const handleManualDeliveryOptionChange = (optId: number | null) => {
    const opt = deliveryOptions.find(o => o.id === optId);
    setManualForm(prev => ({
      ...prev,
      delivery_option: optId,
      shipping_cost: opt ? String(parseFloat(opt.cost).toFixed(0)) : prev.shipping_cost,
    }));
  };

  const handleManualProductSearch = (query: string) => {
    setManualProductSearch(query);
    if (manualSearchRef.current) clearTimeout(manualSearchRef.current);
    if (!query.trim()) { setManualProductResults([]); return; }
    manualSearchRef.current = setTimeout(async () => {
      setManualProductSearching(true);
      try {
        const { productAPI } = await import('../../services/api');
        const res = await productAPI.getProducts({ search: query, page: 1, limit: 10 } as any);
        const results = (res as any).results || res;
        setManualProductResults(Array.isArray(results) ? results : []);
      } catch { setManualProductResults([]); }
      finally { setManualProductSearching(false); }
    }, 300);
  };

  const handleSelectManualProduct = (prod: any) => {
    setManualPickingProduct(prod);
    const p = parseFloat(prod.discount_price || prod.price) || 0;
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: p });
    setManualProductResults([]);
    setManualProductSearch('');
  };

  const handleManualAddToCart = () => {
    if (!manualPickingProduct) return;
    const unitPrice = manualPickAttrs.price || parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price) || 0;
    const existing = manualCartItems.findIndex(
      it => it.product_id === manualPickingProduct.id &&
            it.selected_color === manualPickAttrs.color &&
            it.selected_size === manualPickAttrs.size &&
            it.selected_weight === manualPickAttrs.weight
    );
    if (existing >= 0) {
      const updated = [...manualCartItems];
      updated[existing].quantity = (updated[existing].quantity || 1) + manualPickAttrs.qty;
      setManualCartItems(updated);
    } else {
      setManualCartItems(prev => [...prev, {
        product_id: manualPickingProduct.id,
        product_name: manualPickingProduct.name_en,
        product_image: manualPickingProduct.image || manualPickingProduct.images?.[0]?.image || '',
        product_sku: manualPickingProduct.sku || '',
        product_stock: manualPickingProduct.stock !== undefined ? manualPickingProduct.stock : 99,
        product_attributes: manualPickingProduct.attributes || {},
        quantity: manualPickAttrs.qty,
        price: unitPrice,
        selected_color: manualPickAttrs.color,
        selected_size: manualPickAttrs.size,
        selected_weight: manualPickAttrs.weight,
        selected_attributes: {},
      }]);
    }
    setManualPickingProduct(null);
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setManualProductSearch('');
    setManualProductResults([]);
  };

  const handleUpdateManualItemQty = (index: number, newQty: number) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], quantity: Math.max(1, newQty) };
      }
      return copy;
    });
  };

  const handleUpdateManualItemPrice = (index: number, newPrice: number) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], price: Math.max(0, newPrice) };
      }
      return copy;
    });
  };

  const handleUpdateManualItemVariant = (index: number, field: 'selected_color' | 'selected_size' | 'selected_weight', value: string) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], [field]: value };
      }
      return copy;
    });
  };

  const handleRemoveManualItem = (index: number) => {
    setManualCartItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleManualApplyCoupon = async () => {
    const code = manualForm.coupon_code.trim();
    if (!code) return;
    setManualCouponChecking(true);
    setManualCouponError('');
    setManualCouponApplied(null);
    try {
      const coupons: any = await adminAPI.getCoupons();
      const list = coupons.data?.results || coupons.data || [];
      const found = list.find((c: any) => c.code === code && c.is_active);
      if (found) {
        let disc = 0;
        if (found.discount_type === 'percentage') {
          disc = (manualItemsSubtotal * found.discount_value) / 100;
          if (found.max_discount) disc = Math.min(disc, found.max_discount);
        } else {
          disc = parseFloat(found.discount_value);
        }
        setManualCouponApplied({ code, discount: disc });
      } else {
        setManualCouponError('Invalid or inactive coupon');
      }
    } catch { setManualCouponError('Could not validate coupon'); }
    finally { setManualCouponChecking(false); }
  };

  const handleManualCreateOrder = async (andPrint = false) => {
    setManualError('');
    if (!manualForm.customer_name.trim()) { setManualError('Customer name is required'); return; }
    if (!manualForm.customer_phone.trim()) { setManualError('Phone number is required'); return; }
    if (!manualForm.shipping_address.trim()) { setManualError('Shipping address is required'); return; }
    if (manualCartItems.length === 0) { setManualError('Add at least one product to the order'); return; }

    setManualSaving(true);
    try {
      const payload: any = {
        customer_name: manualForm.customer_name.trim(),
        customer_phone: manualForm.customer_phone.trim(),
        shipping_address: manualForm.shipping_address.trim(),
        notes: manualForm.notes.trim(),
        payment_method: manualForm.payment_method,
        delivery_option: manualForm.delivery_option || null,
        shipping_cost: parseFloat(manualForm.shipping_cost) || 60,
        initial_status: manualForm.initial_status,
        send_email: manualForm.send_email,
        admin_discount_type: manualForm.admin_discount_type || null,
        admin_discount_value: manualForm.admin_discount_value ? parseFloat(manualForm.admin_discount_value) : null,
        traffic_source: manualForm.traffic_source || 'manual',
        utm_campaign: manualForm.utm_campaign.trim(),
        items: manualCartItems.map(it => ({
          product_id: it.product_id,
          quantity: it.quantity || it.qty || 1,
          selected_color: it.selected_color || '',
          selected_size: it.selected_size || '',
          selected_weight: it.selected_weight || '',
          selected_attributes: it.selected_attributes || {},
          unit_price: parseFloat(it.price) || null,
        })),
      };
      if (manualCouponApplied) payload.coupon_code = manualCouponApplied.code;

      const res: any = await adminAPI.createManualOrder(payload);
      const newOrder = res.data;
      setOrders(prev => [newOrder, ...prev]);
      setManualSuccess(`Order #${newOrder.order_number} created successfully!`);

      if (andPrint) {
        setTimeout(() => {
          handlePrintPackingSlip(manualPackingSlipRef);
        }, 300);
      }

      setTimeout(() => {
        setManualModalOpen(false);
        setManualSuccess('');
        setManualCartItems([]);
      }, andPrint ? 2500 : 1200);
    } catch (err: any) {
      const data = err?.response?.data;
      if (typeof data === 'object') {
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        setManualError(msgs);
      } else {
        setManualError('Failed to create order. Please check inputs.');
      }
    } finally {
      setManualSaving(false);
    }
  };

  // ── columnDefs removed: using custom HTML table ──
  // placeholder to mark removal start
  // ── column definitions replaced with custom HTML table ──

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOrders();
    loadDeliveryOptions();
  }, []);

  const loadDeliveryOptions = async () => {
    try {
      const res = await adminAPI.getDeliveryOptions();
      const data = res.data || res;
      const list = Array.isArray(data) ? data : (data.results || []);
      setDeliveryOptions(list);
    } catch (err) {
      console.error('Failed to load delivery options:', err);
    }
  };

  const handleDeliveryOptionChange = (optionId: number | null) => {
    if (optionId === null) {
      setEditForm(prev => ({ ...prev, delivery_option: null }));
      return;
    }
    const found = deliveryOptions.find(opt => opt.id === optionId);
    if (found) {
      setEditForm(prev => ({
        ...prev,
        delivery_option: found.id,
        shipping_cost: String(found.cost),
      }));
    } else {
      setEditForm(prev => ({ ...prev, delivery_option: optionId }));
    }
  };

  // Listen for refresh event from AdminLayout header
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleRefresh = () => {
      loadOrders();
    };
    window.addEventListener('admin-refresh', handleRefresh);
    return () => window.removeEventListener('admin-refresh', handleRefresh);
  }, []);

  // Auto-reload customer history when editingOrder changes (e.g., after Steadfast operations)
  useEffect(() => {
    if (editingOrder && editModalOpen) {
      // Find the latest order data from the orders list
      const updatedOrder = orders.find(o => o.id === editingOrder.id);
      if (updatedOrder) {
        // Update editingOrder with fresh data
        setEditingOrder(updatedOrder);
        // Reload customer history to reflect any new Steadfast deliveries
        if (updatedOrder.customer_phone) {
          loadCustomerHistory(updatedOrder.customer_phone);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, editModalOpen]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrders();
      const data = response.data || response;
      const ordersList = Array.isArray(data) ? data : (data.results || []);
      console.log('Orders loaded:', ordersList.length, 'First order:', ordersList[0]);
      setOrders(ordersList);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSteadfast = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to send this order to Steadfast Courier?')) {
      return;
    }

    setSendingToSteadfast(orderId);
    try {
      const response = await adminAPI.sendOrderToSteadfast(orderId);
      const data = response.data;
      
      if (data.success) {
        alert(`✅ Order sent to Steadfast successfully!\n\nConsignment ID: ${data.consignment_id}\nTracking Code: ${data.tracking_code}`);
        // Reload orders to get updated data
        await loadOrders();
      } else {
        alert(`Failed to send order to Steadfast: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Failed to send order to Steadfast:', error?.response?.status, error?.response?.data || error?.message || error);
      
      // Extract detailed error from response
      const responseData = error.response?.data;
      let errorMessage = 'Failed to send order to Steadfast.';
      
      if (responseData) {
        // Handle case where server returns plain text/HTML instead of JSON
        if (typeof responseData === 'string') {
          // Strip HTML tags if it's an HTML error page
          const cleanText = responseData.replace(/<[^>]*>/g, '').trim().substring(0, 300);
          errorMessage = cleanText || 'Server returned an unexpected error. Check server logs.';
        } else {
          errorMessage = responseData.error || responseData.detail || errorMessage;
        
          // Show validation errors if present
          if (responseData.validation_errors && responseData.validation_errors.length > 0) {
            errorMessage += '\n\nValidation Issues:\n• ' + responseData.validation_errors.join('\n• ');
          }
          
          // Show environment info for debugging
          if (responseData.environment) {
            errorMessage += `\n\nEnvironment: ${responseData.environment}`;
          }
          
          // Show API details if available
          if (responseData.details) {
            const details = responseData.details;
            const apiMessage = details.message || details.raw_response;
            if (apiMessage) {
              errorMessage += `\n\nAPI Message: ${apiMessage}`;
            }
            if (details.errors && typeof details.errors === 'object') {
              const fieldErrors = Object.entries(details.errors)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                .join('\n• ');
              if (fieldErrors) {
                errorMessage += `\n\nField Errors:\n• ${fieldErrors}`;
              }
            }
          }
        }
      } else {
        errorMessage += '\n\n' + (error.message || 'Network error. Please check your connection.');
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setSendingToSteadfast(null);
    }
  };

  // ── Bulk Send to Steadfast (create consignments) ──
  const handleBulkSendToSteadfast = async () => {
    // Selected orders that do NOT have consignment IDs yet (any status)
    const eligible = selectedRows.filter(o => !o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setBulkSending(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkSendToSteadfast(orderIds);
      const data = response.data;
      setBulkSendResult({ sent: data.sent, failed: data.failed, already_sent: data.already_sent ?? 0, results: data.results || [] });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Unknown error';
      setBulkSendResult({ sent: 0, failed: eligible.length, already_sent: 0, results: eligible.map((o: any) => ({ order: o.order_number, success: false, message: msg })) });
    } finally {
      setBulkSending(false);
    }
  };

  // ── Pickup Request (physical pickup / manifest) ──
  const handleBulkPickupRequest = async () => {
    // Selected orders that HAVE consignment IDs (already sent to Steadfast)
    const eligible = selectedRows.filter(o => !!o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setPickupRequesting(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkPickupRequest(orderIds);
      const data = response.data;
      setPickupResult({
        requested: data.requested ?? 0,
        already_requested: data.already_requested ?? 0,
        total_cod: data.total_cod ?? 0,
        pickup_time: data.pickup_time || '',
        results: data.results || [],
        pickup_api_success: data.pickup_api_success,
        pickup_api_error: data.pickup_api_error,
        pickup_id: data.pickup_id,
      });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Unknown error';
      setPickupResult({
        requested: 0, already_requested: 0, total_cod: 0, pickup_time: '',
        results: eligible.map((o: any) => ({ order: o.order_number, consignment_id: o.steadfast_consignment_id, already_requested: false, message: msg })),
      });
    } finally {
      setPickupRequesting(false);
    }
  };

  // Load customer fraud check data by phone
  const loadCustomerHistory = async (phone: string) => {
    if (!phone) {
      setCustomerHistory(null);
      return;
    }
    setCustomerHistoryLoading(true);
    setCustomerHistoryError('');
    try {
      const res = await adminAPI.getCustomerSteadfastHistory(phone);
      setCustomerHistory(res.data || null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '';
      if (msg && !msg.includes('not configured')) {
        setCustomerHistoryError(msg);
      }
    } finally {
      setCustomerHistoryLoading(false);
    }
  };

  const handleEditOrder = async (order: any) => {
    setEditingOrder(order);

    let matchedOptionId = order.delivery_option || null;
    if (!matchedOptionId && deliveryOptions.length > 0) {
      const orderShipCost = parseFloat(order.shipping_cost || 0);
      const matchByCost = deliveryOptions.find(opt => parseFloat(opt.cost) === orderShipCost);
      if (matchByCost) {
        matchedOptionId = matchByCost.id;
      } else {
        matchedOptionId = deliveryOptions[0].id;
      }
    }

    setEditForm({
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      shipping_address: order.shipping_address || '',
      notes: order.notes || '',
      status: order.status || 'pending',
      shipping_cost: order.shipping_cost !== undefined && order.shipping_cost !== null ? String(order.shipping_cost) : '60',
      delivery_option: matchedOptionId,
      traffic_source: order.traffic_source || (order.source === 'manual' ? 'manual' : 'organic'),
      utm_campaign: order.utm_campaign || '',
    });
    setEditError('');
    setCustomerHistory(null);
    setCustomerHistoryError('');

    // Clone order items for editing
    const clonedItems = (order.items || []).map((it: any) => ({
      id: it.id,
      product_id: it.product_id || it.product,
      product_name: it.product_name || 'Product',
      product_image: it.product_image || '',
      product_sku: it.product_sku || '',
      product_stock: it.product_stock !== undefined ? it.product_stock : 99,
      product_attributes: it.product_attributes || {},
      quantity: Number(it.quantity || 1),
      price: parseFloat(it.price || 0),
      subtotal: parseFloat(it.subtotal || (parseFloat(it.price || 0) * Number(it.quantity || 1))),
      selected_color: it.selected_color || '',
      selected_size: it.selected_size || '',
      selected_weight: it.selected_weight || '',
      selected_attributes: it.selected_attributes || {},
    }));
    setEditItems(clonedItems);
    setEditProductSearch('');
    setEditProductResults([]);
    setEditPickingProduct(null);
    setEditPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setEditModalTab('items'); // Default to items editor tab!

    // Reset discount form to current order values
    setDiscountType((order.admin_discount_type as 'flat' | 'percentage' | '') || '');
    setDiscountValue(order.admin_discount_value ? String(order.admin_discount_value) : '');
    setDiscountError('');
    setEditModalOpen(true);
    // Load site settings for packing slip
    try {
      const s = await websiteSettingsAPI.getSettings();
      setSiteSettings({ site_name: s.site_name || '', support_phone: s.support_phone || '', logo_url: s.logo_url || undefined });
    } catch {
      /* non-critical */ }
    // Load customer Steadfast history
    await loadCustomerHistory(order.customer_phone);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingOrder(null);
    setEditError('');
    setEditItems([]);
    setEditProductSearch('');
    setEditProductResults([]);
    setEditPickingProduct(null);
  };

  const handleEditProductSearch = (query: string) => {
    setEditProductSearch(query);
    if (editSearchRef.current) clearTimeout(editSearchRef.current);
    if (!query.trim()) { setEditProductResults([]); return; }
    editSearchRef.current = setTimeout(async () => {
      setEditProductSearching(true);
      try {
        const { productAPI } = await import('../../services/api');
        const res = await productAPI.getProducts({ search: query, page: 1, limit: 10 } as any);
        const results = (res as any).results || res;
        setEditProductResults(Array.isArray(results) ? results : []);
      } catch { setEditProductResults([]); }
      finally { setEditProductSearching(false); }
    }, 350);
  };

  const handleSelectProductToEdit = (prod: any) => {
    setEditPickingProduct(prod);
    const pPrice = parseFloat(prod.discount_price || prod.price || 0);
    const firstColor = prod.attributes?.colors?.enabled && prod.attributes.colors.options?.[0]
      ? (typeof prod.attributes.colors.options[0] === 'string' ? prod.attributes.colors.options[0] : prod.attributes.colors.options[0]?.name || '')
      : '';
    const firstSize = prod.attributes?.sizes?.enabled && prod.attributes.sizes.options?.[0]
      ? (typeof prod.attributes.sizes.options[0] === 'string' ? prod.attributes.sizes.options[0] : prod.attributes.sizes.options[0]?.name || '')
      : '';
    const firstWeight = prod.attributes?.weights?.enabled && prod.attributes.weights.options?.[0]
      ? (typeof prod.attributes.weights.options[0] === 'string' ? prod.attributes.weights.options[0] : prod.attributes.weights.options[0]?.name || '')
      : '';

    setEditPickAttrs({
      color: firstColor,
      size: firstSize,
      weight: firstWeight,
      qty: 1,
      price: pPrice,
    });
  };

  const handleAddProductToEditItems = () => {
    if (!editPickingProduct) return;
    const prod = editPickingProduct;
    const unitPrice = parseFloat(String(editPickAttrs.price)) || parseFloat(prod.discount_price || prod.price || 0);
    const qty = Math.max(1, editPickAttrs.qty);

    const existingIdx = editItems.findIndex(
      it => it.product_id === prod.id &&
            (it.selected_color || '') === (editPickAttrs.color || '') &&
            (it.selected_size || '') === (editPickAttrs.size || '') &&
            (it.selected_weight || '') === (editPickAttrs.weight || '')
    );

    if (existingIdx >= 0) {
      setEditItems(prev => {
        const updated = [...prev];
        const newQty = updated[existingIdx].quantity + qty;
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: newQty,
          subtotal: updated[existingIdx].price * newQty,
        };
        return updated;
      });
    } else {
      const newItem = {
        product_id: prod.id,
        product_name: prod.name_en || prod.name_bn || 'Product',
        product_image: prod.image || prod.images?.[0]?.image || '',
        product_sku: prod.sku || '',
        product_stock: prod.stock !== undefined ? prod.stock : 99,
        product_attributes: prod.attributes || {},
        quantity: qty,
        price: unitPrice,
        subtotal: unitPrice * qty,
        selected_color: editPickAttrs.color || '',
        selected_size: editPickAttrs.size || '',
        selected_weight: editPickAttrs.weight || '',
        selected_attributes: {},
      };
      setEditItems(prev => [...prev, newItem]);
    }

    setEditPickingProduct(null);
    setEditPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setEditProductSearch('');
    setEditProductResults([]);
  };

  const handleUpdateItemQty = (index: number, newQty: number) => {
    if (newQty < 1) return;
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      copy[index] = {
        ...it,
        quantity: newQty,
        subtotal: it.price * newQty,
      };
      return copy;
    });
  };

  const handleUpdateItemPrice = (index: number, newPrice: number) => {
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      const validPrice = Math.max(0, isNaN(newPrice) ? 0 : newPrice);
      copy[index] = {
        ...it,
        price: validPrice,
        subtotal: validPrice * it.quantity,
      };
      return copy;
    });
  };

  const handleUpdateItemVariant = (index: number, field: 'selected_color' | 'selected_size' | 'selected_weight', value: string) => {
    setEditItems(prev => {
      const copy = [...prev];
      const it = copy[index];
      if (!it) return prev;
      copy[index] = {
        ...it,
        [field]: value,
      };
      return copy;
    });
  };

  const handleRemoveEditItem = (index: number) => {
    if (editItems.length <= 1) {
      alert('An order must have at least one product. To cancel the order, change the status to Cancelled.');
      return;
    }
    setEditItems(prev => prev.filter((_, i) => i !== index));
  };

  const handlePrintPackingSlip = (customRef?: any) => {
    const isRef = customRef && typeof customRef === 'object' && 'current' in customRef;
    const content = (isRef && customRef.current) ? customRef.current : packingSlipRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${editingOrder?.order_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', Arial, sans-serif; font-size: 12px; color: #1e293b; background: white; }
          .slip { width: 100%; max-width: 780px; margin: 0 auto; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f1f5f9; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; }
          th, td { padding: 8px 12px; }
          .slip-header { background: linear-gradient(135deg, #0f172a, #1e293b); color: white; padding: 16px 20px; display: flex; justify-content: space-between; align-items: center; }
          .slip-header .store-name { font-size: 16px; font-weight: 800; color: #f8fafc; }
          .slip-header .store-phone { font-size: 11px; color: #94a3b8; margin-top: 2px; }
          .slip-header .courier-label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; text-align: right; }
          .slip-header .courier-name { font-size: 14px; font-weight: 800; color: #f8fafc; text-align: right; margin-top: 2px; }
          .info-row { display: flex; border-bottom: 1px solid #e2e8f0; }
          .ship-to { flex: 1; padding: 14px 18px; border-right: 1px solid #e2e8f0; }
          .order-info { width: 220px; padding: 14px 18px; background: #f8fafc; }
          .section-label { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; }
          .recipient-name { font-size: 15px; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
          .info-table td { padding: 3px 0; font-size: 11px; }
          .info-table .key { color: #64748b; font-weight: 600; padding-right: 10px; white-space: nowrap; }
          .info-table .val { font-weight: 700; color: #1e293b; }
          .items-table thead tr { border-bottom: 2px solid #e2e8f0; }
          .items-table tbody tr { border-bottom: 1px solid #f1f5f9; }
          .items-table tbody tr:nth-child(even) { background: #f8fafc; }
          .totals-wrap { display: flex; justify-content: flex-end; border-top: 1px solid #e2e8f0; }
          .totals-table { width: 260px; }
          .totals-table td { padding: 7px 14px; font-size: 12px; }
          .totals-table .lbl { color: #64748b; }
          .cod-row td { background: linear-gradient(135deg, #0f172a, #1e293b) !important; color: white !important; font-weight: 800; font-size: 14px; padding: 11px 14px; }
          .slip-footer { padding: 10px 18px; background: #f8fafc; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; }
          @page { margin: 10mm; size: A4; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="slip">${content.outerHTML}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplyDiscount = async (remove = false) => {
    if (!editingOrder) return;
    setDiscountSaving(true);
    setDiscountError('');
    try {
      const type = remove ? null : discountType || null;
      const value = remove ? null : (discountValue ? parseFloat(discountValue) : null);
      const res = await adminAPI.applyOrderDiscount(editingOrder.id, type, value);
      const updated = res.data;
      // Patch the local orders array & editingOrder with fresh data
      setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
      setEditingOrder((prev: any) => ({ ...prev, ...updated }));
      if (remove) {
        setDiscountType('');
        setDiscountValue('');
      }
    } catch (err: any) {
      setDiscountError(err?.response?.data?.error || err?.message || 'Failed to apply discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingOrder) return;
    if (editItems.length === 0) {
      setEditError('Order must have at least one product.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const statusChanged = editForm.status !== editingOrder.status;
      // Update core fields & nested items via PATCH
      await adminAPI.updateOrder(editingOrder.id, {
        customer_name: editForm.customer_name,
        customer_phone: editForm.customer_phone,
        shipping_address: editForm.shipping_address,
        notes: editForm.notes,
        shipping_cost: parseFloat(editForm.shipping_cost) || 0,
        delivery_option: editForm.delivery_option || null,
        traffic_source: editForm.traffic_source,
        utm_campaign: editForm.utm_campaign.trim(),
        items: editItems.map(it => ({
          id: it.id,
          product_id: it.product_id,
          quantity: it.quantity,
          price: it.price,
          selected_color: it.selected_color || '',
          selected_size: it.selected_size || '',
          selected_weight: it.selected_weight || '',
          selected_attributes: it.selected_attributes || {},
        })),
      });
      // Update status via dedicated endpoint (triggers email notification)
      if (statusChanged) {
        await adminAPI.updateOrderStatus(editingOrder.id, editForm.status);
      }
      closeEditModal();
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to update order:', error);
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const msg = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        setEditError(msg);
      } else {
        setEditError(error.response?.data?.error || error.message || 'Failed to save changes');
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = async (orderId: number) => {
    const targetOrder = orders.find(o => o.id === orderId);
    const identifier = targetOrder ? `#${targetOrder.order_number}` : `ID ${orderId}`;
    if (!window.confirm(`Are you sure you want to permanently delete order ${identifier}?\n\nThis action cannot be undone and will automatically restore product stock.`)) {
      return;
    }

    try {
      await adminAPI.deleteOrder(orderId);
      if (selectedOrder && selectedOrder.id === orderId) {
        closeViewModal();
      }
      setSelectedRows(prev => prev.filter(id => id !== orderId));
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to delete order:', error);
      alert(`Error: ${error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete order'}`);
    }
  };

  // ── Print Single Label ──
  const handlePrintSingleLabel = async (orderId: number) => {
    setPrintingLabel(orderId);
    try {
      const res = await adminAPI.generateLabels([orderId]);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to print label:', error);
      alert(`Failed to generate label: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setPrintingLabel(null);
    }
  };

  // ── Bulk Print Labels ──
  const handleBulkPrintLabels = async () => {
    if (selectedRows.length === 0) return;
    setBulkPrinting(true);
    try {
      const orderIds = selectedRows.map((o: any) => o.id);
      const res = await adminAPI.generateLabels(orderIds);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to bulk print labels:', error);
      alert(`Failed to generate labels: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setBulkPrinting(false);
    }
  };

  // ── Bulk Category Assignment ──
  // Category → status mapping (same as bulk)
  const CATEGORY_STATUS_MAP: Record<string, string> = {
    new:                 'pending',
    incomplete:          'preorder',
    future:              'preorder',
    not_response:        'no_response',
    confirmed:           'processing',
    confirmed_sent:      'processing',
    in_querier:          'shipped',
    partially_delivered: 'partially_delivered',
    delivered:           'delivered',
    cancelled:           'cancelled',
  };

  // Status → category mapping (reverse) — used as fallback when no steadfast_consignment_id
  const STATUS_CATEGORY_MAP: Record<string, string> = {
    preorder:            'incomplete',
    pending:             'new',
    no_response:         'not_response',
    processing:          'confirmed',
    shipped:             'in_querier',
    in_querier:          'in_querier',
    partially_delivered: 'partially_delivered',
    partial_delivered:   'partially_delivered',
    delivered:           'delivered',
    cancelled:           'cancelled',
  };

  // Smart category resolver — considers courier status, order status and consignment
  const getOrderCategory = (order: any): string => {
    const st = order.status?.toLowerCase();
    const sfSt = order.steadfast_status?.toLowerCase();

    if (st === 'delivered' || sfSt === 'delivered') return 'delivered';
    if (st === 'partially_delivered' || sfSt === 'partial_delivered' || sfSt === 'partially_delivered') return 'partially_delivered';
    if (st === 'cancelled' || sfSt === 'cancelled') return 'cancelled';
    if (st === 'shipped' || st === 'in_querier' || sfSt === 'in_transit') return 'in_querier';
    if (order.steadfast_consignment_id) return 'in_querier';
    return STATUS_CATEGORY_MAP[st] || '';
  };

  const CATEGORY_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
    new:                 { label: '🆕 New',                 color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd' },
    incomplete:          { label: '📋 Incomplete',          color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    future:              { label: '📋 Incomplete',          color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    not_response:        { label: '📞 Not Response',        color: '#b45309', bg: '#fef3c7', border: '#fcd34d' },
    confirmed:           { label: '✅ Confirmed',            color: '#065f46', bg: '#d1fae5', border: '#6ee7b7' },
    confirmed_sent:      { label: '🚚 Confirmed & Sent',    color: '#0f4c75', bg: '#e0f2fe', border: '#7dd3fc' },
    in_querier:          { label: '🚚 In Querier',          color: '#0369a1', bg: '#e0f2fe', border: '#38bdf8' },
    partially_delivered: { label: '📦 Partially Delivered', color: '#c2410c', bg: '#ffedd5', border: '#fdba74' },
    delivered:           { label: '🎉 Delivered',            color: '#15803d', bg: '#dcfce7', border: '#86efac' },
    cancelled:           { label: '❌ Cancelled',            color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' },
  };

  const handleCategoryChange = async (orderId: number, category: string) => {
    const targetStatus = CATEGORY_STATUS_MAP[category];
    if (!targetStatus) return;
    setCategoryUpdating(prev => new Set(prev).add(orderId));
    try {
      const res = await adminAPI.updateOrderStatus(orderId, targetStatus);
      const updatedData = res?.data || res || {};
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, ...updatedData, status: targetStatus } : o
      ));
    } catch (err: any) {
      console.error('Failed to update category:', err);
    } finally {
      setCategoryUpdating(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleBulkCategoryAssign = async () => {
    if (!assignCategory || selectedRows.length === 0) return;
    const statusMap: Record<string, string> = {
      new:                 'pending',
      incomplete:          'preorder',
      future:              'preorder',
      not_response:        'no_response',
      confirmed:           'processing',
      confirmed_sent:      'processing',
      in_querier:          'shipped',
      partially_delivered: 'partially_delivered',
      delivered:           'delivered',
      cancelled:           'cancelled',
    };
    const targetStatus = statusMap[assignCategory];
    if (!targetStatus) return;
    const categoryLabelMap: Record<string, string> = {
      new:                 'New',
      incomplete:          'Incomplete',
      future:              'Incomplete',
      not_response:        'Not Response',
      confirmed:           'Confirmed',
      confirmed_sent:      'Confirmed & Sent',
      in_querier:          'In Querier',
      partially_delivered: 'Partially Delivered',
      delivered:           'Delivered',
      cancelled:           'Cancelled',
    };
    const categoryLabel = categoryLabelMap[assignCategory] ?? assignCategory.charAt(0).toUpperCase() + assignCategory.slice(1);
    if (!window.confirm(`Move ${selectedRows.length} order(s) to "${categoryLabel}"?`)) return;
    setBulkAssigning(true);
    try {
      await Promise.all(selectedRows.map(order => adminAPI.updateOrderStatus(order.id, targetStatus)));
      await loadOrders();
      setAssignCategory('');
    } catch (error: any) {
      console.error('Failed to assign category:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to assign category'}`);
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select orders to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${selectedRows.length} selected order(s)?\n\nThis action cannot be undone and will automatically restore product inventory for active orders.`)) {
      return;
    }

    try {
      await adminAPI.bulkDeleteOrders(selectedRows);
      if (selectedOrder && selectedRows.includes(selectedOrder.id)) {
        closeViewModal();
      }
      setSelectedRows([]);
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to bulk delete orders:', error);
      alert(`Error: ${error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete orders'}`);
    }
  };

  // ── Sync Steadfast Statuses (bulk) ──
  const handleSyncSteadfastStatuses = async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await adminAPI.syncSteadfastStatuses();
      const data = res.data;
      const changed = (data.results || []).filter((r: any) => r.changed).length;
      setLastSyncTime(new Date().toLocaleTimeString());
      setSyncStats({ synced: data.synced || 0, changed });
      // Always reload orders to reflect current state
      await loadOrders();
    } catch (err: any) {
      if (!silent) {
        console.error('Sync failed:', err);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleViewTracking = async (orderId: number, consignmentId: string) => {
    try {
      const response = await adminAPI.getSteadfastTracking(orderId);
      const data = response.data;
      
      if (data.success) {
        // Refresh this order in the grid
        const response2 = await adminAPI.getOrders();
        const d = response2.data || response2;
        const ordersList = Array.isArray(d) ? d : (d.results || []);
        setOrders(ordersList);

        const trackingInfo = data.tracking_data;
        const sfStatus = data.steadfast_status || (typeof trackingInfo === 'string' ? trackingInfo : trackingInfo?.delivery_status) || 'Unknown';
        alert(`Steadfast Status: ${sfStatus.replace(/_/g, ' ')}\n\nConsignment: ${data.consignment_id}\nTracking: ${data.tracking_code}`);
      } else {
        alert(`Failed to get tracking info: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Failed to get tracking info:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to get tracking info'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Ensure orders is always an array before filtering
  const ordersArray = Array.isArray(orders) ? orders : [];

  // Phone numbers that already have an active placed order (not preorder, not cancelled)
  const activePlacedPhones = useMemo(() => {
    const phones = new Set<string>();
    ordersArray.forEach(o => {
      const st = (o.status || '').toLowerCase();
      if (st !== 'preorder' && st !== 'cancelled' && !o.is_preorder) {
        const p = (o.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
        if (p.length >= 8) {
          phones.add(p.slice(-8));
        }
      }
    });
    return phones;
  }, [ordersArray]);

  // Count per group for badges
  const groupCounts: Record<string, number> = { all: ordersArray.length };
  Object.entries(STATUS_GROUPS).forEach(([group, statuses]) => {
    if (group === 'confirmed') {
      // confirmed = processing without courier consignment
      groupCounts[group] = ordersArray.filter(o =>
        statuses.includes(o.status?.toLowerCase()) && !o.steadfast_consignment_id
      ).length;
    } else if (group === 'incomplete' || group === 'future') {
      // Incomplete orders excluding any customer who has already placed a real order
      groupCounts[group] = ordersArray.filter(o => {
        const isPre = statuses.includes(o.status?.toLowerCase()) || o.is_preorder;
        if (!isPre) return false;
        const p = (o.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
        if (p.length >= 8 && activePlacedPhones.has(p.slice(-8))) {
          return false; // Order was placed! Hide from incomplete
        }
        return true;
      }).length;
    } else {
      groupCounts[group] = ordersArray.filter(o => statuses.includes(o.status?.toLowerCase())).length;
    }
  });
  // confirmed_sent = any order that has been sent to courier
  groupCounts['confirmed_sent'] = ordersArray.filter(o => !!o.steadfast_consignment_id).length;
  groupCounts['in_querier'] = ordersArray.filter(o =>
    o.status?.toLowerCase() === 'shipped' ||
    o.status?.toLowerCase() === 'in_querier' ||
    (!!o.steadfast_consignment_id && !['delivered', 'partially_delivered', 'cancelled'].includes(o.status?.toLowerCase()))
  ).length;

  const matchesOrderStatus = (order: any, filter: StatusFilter): boolean => {
    if (filter === 'confirmed_sent') {
      return !!order.steadfast_consignment_id;
    } else if (filter === 'in_querier') {
      return order.status?.toLowerCase() === 'shipped' ||
        order.status?.toLowerCase() === 'in_querier' ||
        (!!order.steadfast_consignment_id && !['delivered', 'partially_delivered', 'cancelled'].includes(order.status?.toLowerCase()));
    } else if (filter === 'confirmed') {
      return ['processing'].includes(order.status?.toLowerCase()) &&
        !order.steadfast_consignment_id;
    } else if (filter === 'incomplete' || filter === 'future') {
      const isPre = order.status?.toLowerCase() === 'preorder' || order.is_preorder;
      const p = (order.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
      if (!isPre || (p.length >= 8 && activePlacedPhones.has(p.slice(-8)))) {
        return false;
      }
      return true;
    } else if (filter !== 'all') {
      const group = STATUS_GROUPS[filter];
      if (group) {
        return group.includes(order.status?.toLowerCase());
      } else {
        return order.status?.toLowerCase() === filter;
      }
    }
    return true;
  };

  const matchesOrderDate = (order: any, filter: StatusFilter, basis: 'auto' | 'created_at' | 'status_date', sDate: string, eDate: string): boolean => {
    if (!sDate && !eDate) return true;
    const orderDate = getOrderFilterDate(order, filter, basis);
    if (!orderDate) return false;
    if (sDate) {
      const start = new Date(sDate);
      start.setHours(0, 0, 0, 0);
      if (orderDate < start) return false;
    }
    if (eDate) {
      const end = new Date(eDate);
      end.setHours(23, 59, 59, 999);
      if (orderDate > end) return false;
    }
    return true;
  };

  const matchesTrafficSource = (order: any, filter: string): boolean => {
    if (!filter || filter === 'all') return true;
    const source = (order.traffic_source || (order.source === 'manual' ? 'manual' : 'organic')).toLowerCase();
    if (filter === 'facebook') return source === 'facebook';
    if (filter === 'tiktok') return source === 'tiktok';
    if (filter === 'google') return source === 'google';
    if (filter === 'whatsapp') return source === 'whatsapp';
    if (filter === 'manual') return source === 'manual';
    if (filter === 'organic') return source === 'organic';
    return true;
  };

  const FacebookIcon = ({ size = 10 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );

  const TikTokIcon = ({ size = 10 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  );

  const GoogleIcon = ({ size = 10 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
      <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"/>
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
    </svg>
  );

  const WhatsAppIcon = ({ size = 10 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );

  const renderTrafficBadge = (order: any) => {
    const source = (order.traffic_source || (order.source === 'manual' ? 'manual' : 'organic')).toLowerCase();
    const campaign = order.utm_campaign;
    const tooltip = campaign
      ? `Platform: ${source.toUpperCase()} | Campaign: ${campaign}`
      : (order.traffic_source_display || source);

    if (source === 'facebook') {
      return (
        <div className="om-source-badge-wrap" title={tooltip}>
          <span className="om-badge om-badge-facebook">
            <FacebookIcon size={10} />
            <span>FB Ads</span>
          </span>
          {campaign && (
            <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
              <Tag size={8} style={{ flexShrink: 0 }} />
              <span className="om-campaign-name">{campaign}</span>
            </span>
          )}
        </div>
      );
    }
    if (source === 'tiktok') {
      return (
        <div className="om-source-badge-wrap" title={tooltip}>
          <span className="om-badge om-badge-tiktok">
            <TikTokIcon size={10} />
            <span>TikTok Ads</span>
          </span>
          {campaign && (
            <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
              <Tag size={8} style={{ flexShrink: 0 }} />
              <span className="om-campaign-name">{campaign}</span>
            </span>
          )}
        </div>
      );
    }
    if (source === 'google') {
      return (
        <div className="om-source-badge-wrap" title={tooltip}>
          <span className="om-badge om-badge-google">
            <GoogleIcon size={10} />
            <span>Google Ads</span>
          </span>
          {campaign && (
            <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
              <Tag size={8} style={{ flexShrink: 0 }} />
              <span className="om-campaign-name">{campaign}</span>
            </span>
          )}
        </div>
      );
    }
    if (source === 'whatsapp') {
      return (
        <div className="om-source-badge-wrap" title={tooltip}>
          <span className="om-badge om-badge-whatsapp">
            <WhatsAppIcon size={10} />
            <span>WhatsApp</span>
          </span>
          {campaign && (
            <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
              <Tag size={8} style={{ flexShrink: 0 }} />
              <span className="om-campaign-name">{campaign}</span>
            </span>
          )}
        </div>
      );
    }
    if (source === 'manual') {
      return (
        <div className="om-source-badge-wrap" title={tooltip || "Manual Phone/Call Order"}>
          <span className="om-badge om-badge-manual">
            <Phone size={9} />
            <span>MANUAL</span>
          </span>
          {campaign && (
            <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
              <Tag size={8} style={{ flexShrink: 0 }} />
              <span className="om-campaign-name">{campaign}</span>
            </span>
          )}
        </div>
      );
    }
    return (
      <div className="om-source-badge-wrap" title={tooltip || "Direct / Organic website order"}>
        <span className="om-badge om-badge-landing">
          <Globe size={9} />
          <span>DIRECT</span>
        </span>
        {campaign && (
          <span className="om-campaign-pill" title={`Campaign: ${campaign}`}>
            <Tag size={8} style={{ flexShrink: 0 }} />
            <span className="om-campaign-name">{campaign}</span>
          </span>
        )}
      </div>
    );
  };

  // Total matching orders across all statuses for the active search query
  const totalSearchMatches = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return 0;
    return ordersArray.filter(o => matchesOrderSearch(o, term)).length;
  }, [ordersArray, searchTerm]);

  // Orders matching active status tab + date + search query + traffic source
  const tabFilteredOrders = useMemo(() => {
    return ordersArray.filter(order => {
      const matchesSearch = matchesOrderSearch(order, searchTerm);
      const matchesStatus = matchesOrderStatus(order, statusFilter);
      const matchesDate = matchesOrderDate(order, statusFilter, dateFilterBasis, startDate, endDate);
      const matchesSource = matchesTrafficSource(order, trafficSourceFilter);
      return matchesSearch && matchesStatus && matchesDate && matchesSource;
    });
  }, [ordersArray, searchTerm, statusFilter, dateFilterBasis, startDate, endDate, trafficSourceFilter]);

  // Smart fallback: if user is searching and 0 matches exist in the selected tab,
  // but matches exist in other categories, show the matching orders automatically!
  const isSearchFallback = Boolean(
    searchTerm.trim() &&
    statusFilter !== 'all' &&
    tabFilteredOrders.length === 0 &&
    totalSearchMatches > 0
  );

  const filteredOrders = useMemo(() => {
    if (isSearchFallback) {
      return ordersArray.filter(order => {
        const matchesSearch = matchesOrderSearch(order, searchTerm);
        const matchesDate = matchesOrderDate(order, 'all', dateFilterBasis, startDate, endDate);
        const matchesSource = matchesTrafficSource(order, trafficSourceFilter);
        return matchesSearch && matchesDate && matchesSource;
      });
    }
    return tabFilteredOrders;
  }, [isSearchFallback, tabFilteredOrders, ordersArray, searchTerm, dateFilterBasis, startDate, endDate, trafficSourceFilter]);

  const ordersToExport = selectedRows.length > 0 ? selectedRows : filteredOrders;

  const handleExportMohammadParcel = () => {
    const suffix = selectedRows.length > 0 ? `selected_${selectedRows.length}` : (statusFilter !== 'all' ? statusFilter : '');
    exportMohammadParcelXLSX(ordersToExport, suffix);
    setExportDropdownOpen(false);
  };

  const handleExportProductDealer = () => {
    const suffix = selectedRows.length > 0 ? `selected_${selectedRows.length}` : (statusFilter !== 'all' ? statusFilter : '');
    exportProductDealerXLSX(ordersToExport, suffix);
    setExportDropdownOpen(false);
  };

  const handleExportStandardXLSX = () => {
    const filterKey = selectedRows.length > 0 ? `selected_${selectedRows.length}` : statusFilter;
    exportStandardXLSX(ordersToExport, filterKey, (order) => CATEGORY_LABELS[getOrderCategory(order) as keyof typeof CATEGORY_LABELS]?.label || '');
    setExportDropdownOpen(false);
  };

  const handleExportStandardCSV = () => {
    const filterKey = selectedRows.length > 0 ? `selected_${selectedRows.length}` : statusFilter;
    exportStandardCSV(ordersToExport, filterKey, (order) => CATEGORY_LABELS[getOrderCategory(order) as keyof typeof CATEGORY_LABELS]?.label || '');
    setExportDropdownOpen(false);
  };

  // ── Sort + Paginate ──


  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a: any, b: any) => {
      let va: any, vb: any;
      if (sortField === 'product_title') {
        va = (a.items?.[0]?.product_name || '').toLowerCase();
        vb = (b.items?.[0]?.product_name || '').toLowerCase();
      } else if (sortField === 'created_at') {
        const da = getOrderFilterDate(a, statusFilter, dateFilterBasis);
        const db = getOrderFilterDate(b, statusFilter, dateFilterBasis);
        va = da ? da.getTime() : 0;
        vb = db ? db.getTime() : 0;
      } else {
        va = (a[sortField] ?? '');
        vb = (b[sortField] ?? '');
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredOrders, sortField, sortDir, statusFilter, dateFilterBasis]);

  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedOrders.length);
  const pagedOrders = sortedOrders.slice(startIndex, endIndex);

  const selectedIds = new Set(selectedRows.map((o: any) => o.id));

  const handleSelectRow = (order: any, checked: boolean) => {
    if (checked) setSelectedRows((prev: any[]) => [...prev, order]);
    else setSelectedRows((prev: any[]) => prev.filter((o: any) => o.id !== order.id));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedRows(pagedOrders);
    else setSelectedRows([]);
  };

  return (
    <div className="admin-page om-page">

      {/* ── Top Action Bar ── */}
      <div className="om-topbar">
        <h1 className="om-page-title">Order Management</h1>
        <div className="om-topbar-right">
          {/* Export Dropdown */}
          <div className="om-export-dropdown-wrap" ref={exportDropdownRef}>
            <button
              type="button"
              className="om-btn om-btn-csv om-export-btn"
              onClick={() => setExportDropdownOpen(prev => !prev)}
              title="Export orders to Excel (.xlsx) or CSV"
            >
              <Download size={13} />
              <span>EXPORT</span>
              <span className="om-btn-badge">{ordersToExport.length}</span>
              <ChevronDown size={12} className={`om-export-chevron ${exportDropdownOpen ? 'open' : ''}`} />
            </button>

            {exportDropdownOpen && (
              <div className="om-export-menu">
                <div className="om-export-menu-header">
                  <span>Export {ordersToExport.length} {selectedRows.length > 0 ? 'Selected' : 'Filtered'} Orders</span>
                </div>
                
                <button
                  type="button"
                  className="om-export-item om-export-item-highlight"
                  onClick={handleExportMohammadParcel}
                >
                  <div className="om-export-item-icon">📦</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Mohammad Parcel <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Invoice Number, Customer Name, Phone, Address, Cod Amount, Note
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="om-export-item om-export-item-highlight"
                  onClick={handleExportProductDealer}
                >
                  <div className="om-export-item-icon">🏷️</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Product Dealer Sheet <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Invoice Number, Customer Name, Customer Phone, Note
                    </div>
                  </div>
                </button>

                <div className="om-export-divider" />

                <button
                  type="button"
                  className="om-export-item"
                  onClick={handleExportStandardXLSX}
                >
                  <div className="om-export-item-icon">📊</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Standard Orders Report <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Full order details, variants, courier status & timestamps
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="om-export-item"
                  onClick={handleExportStandardCSV}
                >
                  <div className="om-export-item-icon">📄</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Standard Orders Export <span className="om-format-tag csv">.CSV</span>
                    </div>
                    <div className="om-export-item-desc">
                      Traditional comma-separated values file
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <button
            className="om-btn om-btn-manual"
            onClick={handleOpenManualModal}
          >
            <Phone size={13} /> New Order
          </button>
          <button
            className="om-btn om-btn-sync"
            onClick={() => handleSyncSteadfastStatuses(false)}
            disabled={syncing}
            title={lastSyncTime ? `Last synced: ${lastSyncTime}` : 'Sync Steadfast delivery statuses'}
          >
            {syncing
              ? <><span className="om-spin"><Loader2 size={13} /></span> Syncing</>
              : <><RefreshCw size={13} /> Sync</>
            }
            {syncStats && !syncing && (
              <span className="om-btn-badge">{syncStats.changed > 0 ? `${syncStats.changed}↑` : <CheckCircle2 size={10} />}</span>
            )}
          </button>

          {/* Bulk: Send to Steadfast */}
          {selectedRows.filter((o: any) => !o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-sf" onClick={handleBulkSendToSteadfast} disabled={bulkSending}>
              {bulkSending ? <span className="om-spin"><Loader2 size={13} /></span> : <Package size={13} />} Send to SF
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Pickup */}
          {selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-pickup" onClick={handleBulkPickupRequest} disabled={pickupRequesting}>
              {pickupRequesting ? <span className="om-spin"><Loader2 size={13} /></span> : <Truck size={13} />} Pickup
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Print Labels */}
          {selectedRows.length > 0 && (
            <button className="om-btn om-btn-print" onClick={handleBulkPrintLabels} disabled={bulkPrinting}>
              {bulkPrinting ? <span className="om-spin"><Loader2 size={13} /></span> : <Printer size={13} />} Print
              <span className="om-btn-badge">{selectedRows.length}</span>
            </button>
          )}
          {/* Bulk: Assign Category */}
          {selectedRows.length > 0 && (
            <>
              <select value={assignCategory} onChange={(e) => setAssignCategory(e.target.value)} className="om-cat-select">
                <option value="">Move to…</option>
                <option value="new">New</option>
                <option value="incomplete">Incomplete</option>
                <option value="not_response">Not Response</option>
                <option value="confirmed">Confirmed</option>
                <option value="confirmed_sent">Conf. &amp; Sent</option>
                <option value="in_querier">In Querier</option>
                <option value="partially_delivered">Partially Delivered</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {assignCategory && (
                <button className="om-btn om-btn-save" onClick={handleBulkCategoryAssign} disabled={bulkAssigning}>
                  {bulkAssigning ? <span className="om-spin"><Loader2 size={13} /></span> : <Save size={13} />} Save
                  <span className="om-btn-badge">{selectedRows.length}</span>
                </button>
              )}
              <button className="om-btn om-btn-del" onClick={handleBulkDelete}>
                <Trash2 size={13} /> {selectedRows.length}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filter Chips ── */}
      <div className="om-filter-wrap">
        <div className="om-filter-status">
          <select 
            className="om-status-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setCurrentPage(1); }}
          >
            {([
              { key: 'all',                 label: 'All',                 count: ordersArray.length },
              { key: 'new',                 label: 'New Orders',          count: groupCounts.new ?? 0 },
              { key: 'confirmed',           label: 'Confirmed',           count: groupCounts.confirmed ?? 0 },
              { key: 'in_querier',          label: 'In Querier',          count: groupCounts.in_querier ?? 0 },
              { key: 'confirmed_sent',      label: 'Ready To Delivery',   count: groupCounts.confirmed_sent ?? 0 },
              { key: 'partially_delivered', label: 'Partially Delivered', count: groupCounts.partially_delivered ?? 0 },
              { key: 'delivered',           label: 'Delivered',           count: groupCounts.delivered ?? 0 },
              { key: 'not_response',        label: 'Not Response',        count: groupCounts.not_response ?? 0 },
              { key: 'cancelled',           label: 'Cancelled',           count: groupCounts.cancelled ?? 0 },
              { key: 'incomplete',          label: 'Incomplete',          count: (groupCounts.incomplete ?? groupCounts.future ?? 0) },
            ] as { key: string; label: string; count: number }[]).map(chip => (
              <option key={chip.key} value={chip.key}>
                {chip.label} : {chip.count}
              </option>
            ))}
          </select>
        </div>

        <div className="om-filter-row2">
          <div className="om-date-range">
            <select
              value={dateFilterBasis}
              onChange={e => { setDateFilterBasis(e.target.value as any); setCurrentPage(1); }}
              className="om-date-basis-select"
              title="Filter date basis"
            >
              <option value="auto">
                {statusFilter === 'confirmed' ? '⚡ Confirmed Date' :
                 statusFilter === 'in_querier' || statusFilter === 'confirmed_sent' ? '🚚 Sent to Courier Date' :
                 statusFilter === 'delivered' ? '🎉 Delivered Date' :
                 statusFilter === 'cancelled' ? '❌ Cancelled Date' :
                 '📅 Stage Date (Auto)'}
              </option>
              <option value="created_at">📅 Placed Date</option>
              <option value="status_date">🕒 Status Date</option>
            </select>
            <input
              type="date" value={startDate}
              onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="om-date-input" title="From date"
            />
            <span className="om-date-sep">to</span>
            <input
              type="date" value={endDate}
              onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="om-date-input" title="To date"
            />
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); }} className="om-date-clear"><X size={11} /></button>
            )}
          </div>

          {/* Traffic Source Filter */}
          <div className="om-traffic-source-wrap">
            <select
              value={trafficSourceFilter}
              onChange={e => { setTrafficSourceFilter(e.target.value); setCurrentPage(1); }}
              className="om-traffic-source-select"
              title="Filter by Traffic Source / Ad Platform"
            >
              <option value="all">🎯 All Sources ({ordersArray.length})</option>
              <option value="facebook">🟦 Facebook ({ordersArray.filter(o => matchesTrafficSource(o, 'facebook')).length})</option>
              <option value="tiktok">🎵 TikTok ({ordersArray.filter(o => matchesTrafficSource(o, 'tiktok')).length})</option>
              <option value="whatsapp">💬 WhatsApp ({ordersArray.filter(o => matchesTrafficSource(o, 'whatsapp')).length})</option>
              <option value="google">🔍 Google Ads ({ordersArray.filter(o => matchesTrafficSource(o, 'google')).length})</option>
              <option value="organic">🌐 Direct / Website ({ordersArray.filter(o => matchesTrafficSource(o, 'organic')).length})</option>
              <option value="manual">📞 Manual ({ordersArray.filter(o => matchesTrafficSource(o, 'manual')).length})</option>
            </select>
          </div>

          <div className="om-search-wrap">
            <span className="om-search-icon"><Search size={13} /></span>
            <input
              type="text"
              placeholder="Search by mobile number, order ID, customer..."
              value={searchTerm}
              onChange={handleSearch}
              className="om-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="om-search-clear"
                onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Search Feedback Banner ── */}
      {searchTerm.trim() && statusFilter !== 'all' && totalSearchMatches > 0 && (
        isSearchFallback ? (
          <div className="om-search-banner is-fallback">
            <span>
              ℹ️ No orders in <strong>{STATUS_FILTER_LABELS[statusFilter] || statusFilter}</strong> match &ldquo;<strong>{searchTerm}</strong>&rdquo;. Showing <strong>{totalSearchMatches}</strong> matching order{totalSearchMatches > 1 ? 's' : ''} found in other categories.
            </span>
            <button
              type="button"
              className="om-search-banner-btn"
              onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            >
              Switch to All Categories
            </button>
          </div>
        ) : (
          totalSearchMatches > tabFilteredOrders.length ? (
            <div className="om-search-banner">
              <span>
                Showing <strong>{tabFilteredOrders.length}</strong> matching order{tabFilteredOrders.length > 1 ? 's' : ''} in <strong>{STATUS_FILTER_LABELS[statusFilter] || statusFilter}</strong>. (<strong>{totalSearchMatches - tabFilteredOrders.length}</strong> more found in other categories).
              </span>
              <button
                type="button"
                className="om-search-banner-btn"
                onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
              >
                View All Categories ({totalSearchMatches})
              </button>
            </div>
          ) : null
        )
      )}

      {/* ── MOBILE CARD LIST ── */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading orders…</div>}
          {!loading && filteredOrders.length === 0 && <div className="mc-empty">No orders found.</div>}
          {!loading && filteredOrders.map((order) => {
            const firstItem = order.items?.[0] || {};
            const imgUrl = firstItem.product_image || firstItem.image || '';
            const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
            const extraItems = (order.items?.length || 1) - 1;
            const statusKey = (order.status || 'pending').toLowerCase();
            const statusClass = statusKey.replace(/_/g, '-');
            const statusLabel = (order.status || 'Pending').replace(/_/g, ' ');
            const color  = firstItem.selected_color  || '';
            const size   = firstItem.selected_size   || '';
            const weight = firstItem.selected_weight || '';
            const isSending  = sendingToSteadfast === order.id;
            const isPrinting = printingLabel === order.id;
            const currentCategory = getOrderCategory(order);
            const categoryConfig  = CATEGORY_LABELS[currentCategory];
            return (
              <div className="mobile-card" key={order.id}>
                <div className="mc-top">
                  {imgUrl ? (
                    <img className="mc-thumb" src={imgUrl} alt="Product" />
                  ) : (
                    <div className="mc-thumb-placeholder">📦</div>
                  )}
                  <div className="mc-info">
                    <div className="mc-title">{renderHighlightedText(order.order_number, searchTerm)}</div>
                    <div className="mc-subtitle">{productName}{extraItems > 0 ? ` (+${extraItems} more)` : ''}</div>
                    <div style={{ marginTop: '4px' }}>{renderTrafficBadge(order)}</div>
                  </div>
                  <div className="mc-badge-corner">
                    <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
                  </div>
                </div>
                {(color || size || weight) && (
                  <div className="mc-variants">
                    {color && <span className="mc-variant-pill" style={{ background: '#f5f3ff', color: '#7c3aed', borderColor: '#ddd6fe' }}><span className="mc-variant-color-dot" style={{ background: color.toLowerCase() }} />{color}</span>}
                    {size && <span className="mc-variant-pill" style={{ background: '#EEF2FF', color: '#4F46E5', borderColor: '#C7D2FE' }}>{size}</span>}
                    {weight && <span className="mc-variant-pill" style={{ background: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' }}>{weight}</span>}
                  </div>
                )}
                <div className="mc-divider" />
                <div className="mc-meta">
                  <div className="mc-meta-item"><span className="mc-meta-label">👤</span><span className="mc-meta-value">{renderHighlightedText(order.customer_name, searchTerm)}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">📱</span><span className="mc-meta-value">{renderHighlightedPhone(order.customer_phone, searchTerm)}</span></div>
                  <div className="mc-meta-item"><span className="mc-meta-label">💰</span>
                    <span className="mc-meta-value">
                      {parseFloat(order.discount_amount || 0) > 0
                        ? <>৳{parseFloat(order.final_amount || order.total_amount || 0).toFixed(0)} <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '10px' }}>৳{parseFloat(order.total_amount || 0).toFixed(0)}</span></>
                        : `৳${parseFloat(order.total_amount || 0).toFixed(0)}`
                      }
                    </span>
                  </div>
                  <div className="mc-meta-item"><span className="mc-meta-label">📅</span><span className="mc-meta-value">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</span></div>
                  {order.confirmed_at && (
                    <div className="mc-meta-item" title={`Confirmed: ${new Date(order.confirmed_at).toLocaleString()}`}>
                      <span className="mc-meta-label">⚡</span>
                      <span className="mc-meta-value" style={{ color: '#16a34a', fontWeight: 600 }}>
                        {new Date(order.confirmed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  )}
                </div>
                {order.shipping_address && <div className="mc-address">📍 {order.shipping_address}</div>}
                <div style={{ marginTop: '8px' }}>
                  <select
                    className="mc-status-select"
                    value={currentCategory}
                    onChange={(e) => handleCategoryChange(order.id, e.target.value)}
                    disabled={categoryUpdating.has(order.id)}
                    style={{ borderColor: categoryConfig?.border || '#e5e7eb', background: categoryConfig?.bg || '#f9fafb', color: categoryConfig?.color || '#374151' }}
                  >
                    <option value="">— Category —</option>
                    <option value="new">New</option>
                    <option value="incomplete">Incomplete</option>
                    <option value="not_response">Not Response</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="confirmed_sent">Conf. &amp; Sent</option>
                    <option value="in_querier">In Querier</option>
                    <option value="partially_delivered">Partially Delivered</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="mc-actions">
                  <button className="mc-btn mc-btn-view" onClick={() => handleViewOrder(order)}>View</button>
                  <button className="mc-btn mc-btn-edit" onClick={() => handleEditOrder(order)}>Edit</button>
                  <button className="mc-btn mc-btn-print" onClick={() => handlePrintSingleLabel(order.id)} disabled={isPrinting}>{isPrinting ? 'Printing…' : 'Print'}</button>
                  <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteOrder(order.id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP TABLE ── */}
      {!isMobile && (
        <div className="om-table-card">
          <div className="om-table-scroll">
            <table className="om-table">
              <colgroup>
                <col className="col-check" />
                <col className="col-product" />
                <col className="col-order" />
                <col className="col-customer" />
                <col className="col-address" />
                <col className="col-category" />
                <col className="col-variant" />
                <col className="col-finance" />
                <col className="col-status" />
                <col className="col-sf" />
                <col className="col-notes" />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="om-th om-th-check">
                    <input
                      type="checkbox"
                      className="om-checkbox"
                      checked={pagedOrders.length > 0 && selectedIds.size === pagedOrders.length}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  {([
                    { key: 'product',          label: 'Product',    sortKey: 'product_title' },
                    { key: 'order_number',     label: 'Order #',    sortKey: 'order_number' },
                    { key: 'customer',         label: 'Customer',   sortKey: 'customer_name' },
                    { key: 'shipping_address', label: 'Address',    sortKey: 'shipping_address' },
                    { key: 'category',         label: 'Category',   sortKey: '' },
                    { key: 'variant',          label: 'Variant',    sortKey: '' },
                    { key: 'total_amount',     label: 'Financials', sortKey: 'total_amount' },
                    { key: 'status',           label: 'Status',     sortKey: 'status' },
                    { key: 'steadfast',        label: 'Steadfast',  sortKey: '' },
                    { key: 'notes',            label: 'Notes',      sortKey: '' },
                    { key: 'actions',          label: 'Actions',    sortKey: '' },
                  ] as { key: string; label: string; sortKey: string }[]).map(col => (
                    <th
                      key={col.key}
                      className={`om-th${col.sortKey ? ' om-th-sort' : ''}`}
                      onClick={() => {
                        if (!col.sortKey) return;
                        if (sortField === col.sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortField(col.sortKey); setSortDir('asc'); }
                        setCurrentPage(1);
                      }}
                    >
                      {col.label}
                      {sortField === col.sortKey && col.sortKey
                        ? <span className="om-sort-arrow">{sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</span>
                        : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="om-loading">Loading orders…</td></tr>
                )}
                {!loading && pagedOrders.length === 0 && (
                  <tr><td colSpan={11} className="om-empty">No orders found.</td></tr>
                )}
                {!loading && pagedOrders.map(order => {
                  const firstItem = order.items?.[0] || {};
                  const imgUrl = firstItem.product_image || firstItem.image || '';
                  const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
                  const sku = firstItem.sku || '';
                  const extraItems = (order.items?.length || 1) - 1;
                  const color  = firstItem.selected_color  || '';
                  const size   = firstItem.selected_size   || '';
                  const weight = firstItem.selected_weight || '';
                  const total    = parseFloat(order.total_amount    || 0);
                  const discount = parseFloat(order.discount_amount || 0);
                  const final    = parseFloat(order.final_amount    || total);
                  const shipping = parseFloat(order.shipping_cost   || 0);
                  const isSelected  = selectedIds.has(order.id);
                  const isSending   = sendingToSteadfast === order.id;
                  const isPrinting  = printingLabel === order.id;
                  const currentCategory = getOrderCategory(order);
                  const catCfg = CATEGORY_LABELS[currentCategory];

                  return (
                    <tr key={order.id} className={`om-row${isSelected ? ' om-row-selected' : ''}`}>

                      {/* ── Checkbox ── */}
                      <td className="om-td om-td-check">
                        <input
                          type="checkbox"
                          className="om-checkbox"
                          checked={isSelected}
                          onChange={e => handleSelectRow(order, e.target.checked)}
                        />
                      </td>

                      {/* ── Product ── */}
                      <td className="om-td om-td-product">
                        <div className="om-product-cell">
                          {imgUrl
                            ? <img src={imgUrl} alt="" className="om-product-img" />
                            : <div className="om-product-img-placeholder"><Box size={16} /></div>
                          }
                          <div className="om-product-info">
                            <span className="om-product-name">
                              {productName}{extraItems > 0 ? ` (+${extraItems})` : ''}
                            </span>
                            {sku && <span className="om-product-sku">SKU: {sku}</span>}
                            {renderTrafficBadge(order)}
                          </div>
                        </div>
                      </td>

                      {/* ── Order # ── */}
                      <td className="om-td">
                        <div className="om-order-num">{renderHighlightedText(order.order_number, searchTerm)}</div>
                        <div className="om-order-date" title="Order Placed Date">
                          📅 {order.created_at
                            ? new Date(order.created_at).toLocaleString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })
                            : '—'}
                        </div>
                        {order.confirmed_at && (
                          <div className="om-stage-badge om-stage-confirmed" title={`Confirmed: ${new Date(order.confirmed_at).toLocaleString()}`}>
                            ⚡ Conf: {new Date(order.confirmed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                        {order.shipped_at && (
                          <div className="om-stage-badge om-stage-shipped" title={`Sent to Courier: ${new Date(order.shipped_at).toLocaleString()}`}>
                            🚚 Courier: {new Date(order.shipped_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                        {order.delivered_at && (
                          <div className="om-stage-badge om-stage-delivered" title={`Delivered: ${new Date(order.delivered_at).toLocaleString()}`}>
                            🎉 Deliv: {new Date(order.delivered_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </td>

                      {/* ── Customer ── */}
                      <td className="om-td">
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.82rem', marginBottom: '1px' }}>
                          {renderHighlightedText(order.customer_name, searchTerm)}
                        </div>
                        <div style={{ color: '#2563eb', fontSize: '0.75rem', fontWeight: '500' }}>
                          {renderHighlightedPhone(order.customer_phone, searchTerm)}
                        </div>
                      </td>

                      {/* ── Address ── */}
                      <td className="om-td om-td-address">
                        <div className="om-address-text" style={{ lineHeight: '1.3', fontSize: '0.75rem' }}>{order.shipping_address || '—'}</div>
                      </td>

                      {/* ── Category ── */}
                      <td className="om-td">
                        {categoryUpdating.has(order.id) ? (
                          <span className="om-saving">Saving…</span>
                        ) : (
                          <select
                            value={currentCategory}
                            onChange={e => handleCategoryChange(order.id, e.target.value)}
                            className="om-cat-dropdown"
                            style={{
                              background: catCfg?.bg || '#f9fafb',
                              color: catCfg?.color || '#374151',
                              borderColor: catCfg?.border || '#e5e7eb',
                            }}
                          >
                            <option value="">— Select —</option>
                            <option value="new">New</option>
                            <option value="incomplete">Incomplete</option>
                            <option value="not_response">Not Response</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="confirmed_sent">Conf. &amp; Sent</option>
                            <option value="in_querier">In Querier</option>
                            <option value="partially_delivered">Partially Delivered</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </td>

                      {/* ── Variant ── */}
                      <td className="om-td om-td-variant">
                        <div className="om-variant-stack">
                          {color && (
                            <span className="om-chip-var om-chip-color">
                              <span className="om-color-dot" style={{ background: color.toLowerCase() }} />
                              {color}
                            </span>
                          )}
                          {size   && <span className="om-chip-var om-chip-size">{size}</span>}
                          {weight && <span className="om-chip-var om-chip-weight">{weight}</span>}
                          {!color && !size && !weight && <span className="om-no-variant">—</span>}
                        </div>
                      </td>

                      {/* ── Financials ── */}
                      <td className="om-td om-td-finance">
                        {discount > 0 ? (
                          <>
                            <span className="om-price-final">৳{final.toFixed(2)}</span>
                            <span className="om-price-orig">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <span className="om-price-main">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        )}
                      </td>

                      {/* ── Status ── */}
                      <td className="om-td">
                        <span className={`om-status-chip om-status-${(order.status || 'pending').toLowerCase().replace(/_/g, '-')}`}>
                          {(order.status || 'Pending').replace(/_/g, ' ')}
                        </span>
                      </td>

                      {/* ── Steadfast ── */}
                      <td className="om-td om-td-sf">
                        {order.steadfast_tracking_code ? (
                          <>
                            <span className="om-sf-label">Courier tracking&</span>
                            <span className="om-sf-consignment">
                              Consignment ID: {order.steadfast_consignment_id || '—'}
                            </span>
                          </>
                        ) : (
                          <span className="om-no-variant">—</span>
                        )}
                      </td>

                      {/* ── Notes ── */}
                      <td className="om-td om-td-notes">
                        <span className="om-notes-text">{order.notes || '—'}</span>
                      </td>

                      {/* ── Actions ── */}
                      <td className="om-td om-td-actions">
                        <div className="om-actions-row">
                          <button
                            className="om-action-btn om-action-print"
                            title="Print Label"
                            onClick={() => handlePrintSingleLabel(order.id)}
                            disabled={isPrinting}
                          >
                            {isPrinting ? <span className="om-spin"><Loader2 size={12} /></span> : <Printer size={12} />}
                          </button>
                          <button
                            className="om-action-btn om-action-edit"
                            title="Edit Order"
                            onClick={() => handleEditOrder(order)}
                          >
                            <Pencil size={12} />
                          </button>
                          {order.steadfast_consignment_id && (
                            <button
                              className="om-action-btn om-action-track"
                              title="Track"
                              onClick={() => handleViewTracking(order.id, order.steadfast_consignment_id)}
                            >
                              <MapPin size={12} />
                            </button>
                          )}
                          <button
                            className="om-action-btn om-action-view"
                            title="View Details"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            className="om-action-btn om-action-del"
                            title="Delete Order"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* ── Pagination (Shared for Mobile & Desktop) ── */}
      {sortedOrders.length > 0 && (
        <div className="om-pagination">
          <div className="om-page-size-selector">
            <span className="om-page-size-label">Rows per page:</span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="om-page-size-select"
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          
          <div className="om-page-info">
            {startIndex + 1}-{endIndex} of {sortedOrders.length} orders
          </div>

          <div className="om-page-controls">
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safeCurrentPage === 1}
              title="Previous Page"
            >
              ‹
            </button>
            <div className="om-page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = safeCurrentPage;
                if (totalPages <= 5) pageNum = i + 1;
                else if (safeCurrentPage <= 3) pageNum = i + 1;
                else if (safeCurrentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = safeCurrentPage - 2 + i;
                
                return (
                  <button
                    key={pageNum}
                    className={`om-page-num ${pageNum === safeCurrentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage === totalPages}
              title="Next Page"
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* ─── Manual Order Modal (Full Parity with Edit Modal) ─── */}
      {manualModalOpen && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.72)',
          backdropFilter: 'blur(5px)',
          zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '8px' : '18px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            width: isMobile ? '100%' : '95vw',
            maxWidth: '1240px',
            height: isMobile ? '96vh' : '92vh',
            maxHeight: '94vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              padding: '12px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '8px',
                  background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', fontSize: '1.1rem'
                }}>
                  ➕
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 style={{ color: '#f8fafc', margin: 0, fontSize: '1.05rem', fontWeight: '700', letterSpacing: '-0.02em' }}>
                      Create Manual Order
                    </h2>
                    <span style={{ fontSize: '0.72rem', background: '#3b82f6', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontWeight: '700' }}>
                      Phone / Call-in
                    </span>
                  </div>
                  <p style={{ color: '#94a3b8', margin: '2px 0 0', fontSize: '0.72rem' }}>
                    Direct admin order entry with live inventory, custom pricing, courier packing slip &amp; fraud check
                  </p>
                </div>
              </div>

              {/* Right Side Top Header Section: Actions & Close */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {/* ✓ Create Order Button */}
                <button
                  type="button"
                  onClick={() => handleManualCreateOrder(false)}
                  disabled={manualSaving || manualCartItems.length === 0}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 18px',
                    borderRadius: '8px',
                    background: (manualSaving || manualCartItems.length === 0)
                      ? '#475569'
                      : 'linear-gradient(135deg, #4f46e5, #4338ca)',
                    color: (manualSaving || manualCartItems.length === 0) ? '#94a3b8' : '#fff',
                    border: 'none',
                    fontSize: isMobile ? '0.78rem' : '0.86rem',
                    fontWeight: '700',
                    cursor: (manualSaving || manualCartItems.length === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: (manualSaving || manualCartItems.length === 0) ? 'none' : '0 3px 12px rgba(79, 70, 229, 0.4)',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                  title="Create and save manual order"
                >
                  {manualSaving ? 'Creating...' : '✓ Create Order'}
                </button>

                {/* 🖨️ Create & Print Packing Slip Button */}
                <button
                  type="button"
                  onClick={() => handleManualCreateOrder(true)}
                  disabled={manualSaving || manualCartItems.length === 0}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 14px',
                    borderRadius: '8px',
                    background: (manualSaving || manualCartItems.length === 0) ? '#1e293b' : '#0f172a',
                    color: (manualSaving || manualCartItems.length === 0) ? '#64748b' : '#f8fafc',
                    border: '1px solid rgba(255,255,255,0.2)',
                    fontSize: isMobile ? '0.74rem' : '0.82rem',
                    fontWeight: '700',
                    cursor: (manualSaving || manualCartItems.length === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                  title="Create order and open printable Steadfast packing slip"
                >
                  🖨️ {isMobile ? 'Print Slip' : 'Create & Print Packing Slip'}
                </button>

                <button
                  type="button"
                  onClick={() => setManualModalOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                    color: '#fff', borderRadius: '7px', width: '30px', height: '30px',
                    fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', transition: 'all 0.15s',
                    marginLeft: '2px', flexShrink: 0
                  }}
                  title="Close modal"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Mobile Tab Bar */}
            {isMobile && (
              <div style={{
                display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0
              }}>
                {([
                  { key: 'form', label: '📋 Order Form' },
                  { key: 'items', label: `🛒 Items (${manualCartItems.length})` },
                  { key: 'slip', label: '🧾 Slip' },
                  { key: 'fraud', label: '🛡️ Fraud' },
                ] as { key: 'items' | 'form' | 'slip' | 'fraud'; label: string }[]).map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setManualModalTab(tab.key)}
                    style={{
                      flex: 1, padding: '10px 4px', fontSize: '0.78rem', fontWeight: '700',
                      border: 'none', background: manualModalTab === tab.key ? '#fff' : 'transparent',
                      color: manualModalTab === tab.key ? '#2563eb' : '#64748b',
                      borderBottom: manualModalTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                      cursor: 'pointer'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Body */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {/* ── LEFT: Manual Order Form Sidebar ── */}
              {(!isMobile || manualModalTab === 'form') && (
                <div style={{
                  width: isMobile ? '100%' : '360px',
                  minWidth: isMobile ? undefined : '330px',
                  flexShrink: 0,
                  padding: '16px 18px', overflowY: 'auto',
                  borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  {/* Move to Category / Initial Status Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #3b82f6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        MOVE TO CATEGORY
                      </span>
                      {(() => {
                        const cur = EDIT_STATUS_OPTIONS.find(st => st.value === manualForm.initial_status) || {
                          label: manualForm.initial_status,
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      value={manualForm.initial_status}
                      onChange={e => setManualForm(p => ({ ...p, initial_status: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      {EDIT_STATUS_OPTIONS.map(st => (
                        <option key={st.value} value={st.value}>
                          {st.icon} {st.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Order Platform / Source Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #8b5cf6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        ORDER PLATFORM / SOURCE
                      </span>
                      {(() => {
                        const cur = TRAFFIC_PLATFORM_OPTIONS.find(p => p.value === manualForm.traffic_source) || TRAFFIC_PLATFORM_OPTIONS[0];
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      value={manualForm.traffic_source}
                      onChange={e => setManualForm(p => ({ ...p, traffic_source: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '8px'
                      }}
                    >
                      {TRAFFIC_PLATFORM_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Campaign / Ad Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={manualForm.utm_campaign}
                        onChange={e => setManualForm(p => ({ ...p, utm_campaign: e.target.value }))}
                        placeholder="e.g. Eid Campaign, FB Ad 1, Post 2..."
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '0.82rem',
                          color: '#1e293b',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <p style={{ margin: '4px 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '4px', display: 'inline-block' }}>
                    Customer Details
                  </p>

                  {manualError && (
                    <div style={{
                      background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px'
                    }}>
                      ⚠️ {manualError}
                    </div>
                  )}

                  {manualSuccess && (
                    <div style={{
                      background: '#dcfce7', color: '#15803d', border: '1px solid #86efac',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px', fontWeight: '600'
                    }}>
                      ✅ {manualSuccess}
                    </div>
                  )}

                  {/* Customer Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={manualForm.customer_name}
                      onChange={e => setManualForm(p => ({ ...p, customer_name: e.target.value }))}
                      placeholder="Customer full name"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Customer Phone + Fraud Badge */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Phone Number *
                      </label>
                      {customerHistoryLoading ? (
                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>Checking fraud...</span>
                      ) : customerHistory?.fraud_check ? (() => {
                        const sf = customerHistory.fraud_check.steadfast || {};
                        const tot = sf.total || 0;
                        const del = sf.delivery || 0;
                        const sRate = tot > 0 ? Math.round((del / tot) * 100) : null;
                        const isRisk = tot >= 3 && sRate !== null && sRate < 60;
                        const isGood = tot >= 2 && sRate !== null && sRate >= 80;
                        return (
                          <span
                            onClick={() => setManualModalTab('fraud')}
                            style={{
                              cursor: 'pointer',
                              fontSize: '0.68rem',
                              fontWeight: '700',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: isRisk ? '#fef2f2' : isGood ? '#f0fdf4' : '#eff6ff',
                              color: isRisk ? '#b91c1c' : isGood ? '#15803d' : '#2563eb',
                              border: `1px solid ${isRisk ? '#fca5a5' : isGood ? '#86efac' : '#bfdbfe'}`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                            title="Click to view full fraud intelligence"
                          >
                            {isRisk ? '⚠️ High Return Risk' : isGood ? `🟢 ${sRate}% Success` : sRate !== null ? `🔵 ${sRate}% Success` : 'ℹ️ New Phone'} (View)
                          </span>
                        );
                      })() : null}
                    </div>
                    <input
                      type="text"
                      value={manualForm.customer_phone}
                      onChange={e => handleManualPhoneChange(e.target.value)}
                      placeholder="01XXXXXXXXX"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Shipping Address */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Shipping Address *
                    </label>
                    <textarea
                      value={manualForm.shipping_address}
                      onChange={e => setManualForm(p => ({ ...p, shipping_address: e.target.value }))}
                      placeholder="Full delivery address, area, city"
                      rows={2}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* Order Notes */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Order Notes (Optional)
                    </label>
                    <input
                      type="text"
                      value={manualForm.notes}
                      onChange={e => setManualForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Any customer preferences or instructions"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Delivery Option & Shipping Fee */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🚚 Delivery Option &amp; Shipping Fee
                    </p>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                        Shipping Option
                      </label>
                      <select
                        value={manualForm.delivery_option || (deliveryOptions[0]?.id || '')}
                        onChange={e => {
                          const optId = e.target.value ? parseInt(e.target.value) : null;
                          handleManualDeliveryOptionChange(optId);
                        }}
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '600',
                          backgroundColor: '#f8fafc', cursor: 'pointer', outline: 'none', boxSizing: 'border-box'
                        }}
                      >
                        {deliveryOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon || '🚚'} {opt.name_en} — ৳{parseFloat(opt.cost).toFixed(0)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Shipping Cost (৳)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={manualForm.shipping_cost}
                        onChange={e => setManualForm(p => ({ ...p, shipping_cost: e.target.value }))}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '700', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Admin Discount */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🏷️ Admin Discount
                    </p>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setManualForm(p => ({ ...p, admin_discount_type: p.admin_discount_type === 'flat' ? null : 'flat' }))}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700',
                          border: manualForm.admin_discount_type === 'flat' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                          background: manualForm.admin_discount_type === 'flat' ? '#eff6ff' : '#f8fafc',
                          color: manualForm.admin_discount_type === 'flat' ? '#1d4ed8' : '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        ৳ Flat
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualForm(p => ({ ...p, admin_discount_type: p.admin_discount_type === 'percentage' ? null : 'percentage' }))}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700',
                          border: manualForm.admin_discount_type === 'percentage' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                          background: manualForm.admin_discount_type === 'percentage' ? '#eff6ff' : '#f8fafc',
                          color: manualForm.admin_discount_type === 'percentage' ? '#1d4ed8' : '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        % Percentage
                      </button>
                    </div>
                    {manualForm.admin_discount_type && (
                      <div>
                        <input
                          type="number"
                          min="0"
                          placeholder={manualForm.admin_discount_type === 'flat' ? 'Discount in ৳' : 'Discount % (e.g. 10)'}
                          value={manualForm.admin_discount_value}
                          onChange={e => setManualForm(p => ({ ...p, admin_discount_value: e.target.value }))}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '700', outline: 'none', boxSizing: 'border-box' }}
                        />
                        {manualAdminDiscount > 0 && (
                          <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: '4px', fontWeight: '600' }}>
                            ✓ −৳{manualAdminDiscount.toFixed(2)} applied
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Coupon Code */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Coupon Code (Optional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        value={manualForm.coupon_code}
                        onChange={e => {
                          setManualForm(p => ({ ...p, coupon_code: e.target.value }));
                          setManualCouponApplied(null);
                          setManualCouponError('');
                        }}
                        placeholder="Enter coupon"
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', border: `1px solid ${manualCouponApplied ? '#86efac' : manualCouponError ? '#fca5a5' : '#cbd5e1'}`, fontSize: '0.82rem', outline: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={handleManualApplyCoupon}
                        disabled={manualCouponChecking || !manualForm.coupon_code.trim()}
                        style={{ padding: '7px 12px', borderRadius: '7px', background: '#475569', color: '#fff', border: 'none', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {manualCouponChecking ? '...' : 'Apply'}
                      </button>
                    </div>
                    {manualCouponApplied && <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: '3px' }}>✅ Coupon applied: −৳{manualCouponApplied.discount.toFixed(2)}</div>}
                    {manualCouponError && <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '3px' }}>⚠️ {manualCouponError}</div>}
                  </div>

                  {/* Send Confirmation Email */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.78rem', color: '#374151', marginBottom: '14px' }}>
                    <input
                      type="checkbox"
                      checked={manualForm.send_email}
                      onChange={e => setManualForm(p => ({ ...p, send_email: e.target.checked }))}
                      style={{ width: '15px', height: '15px' }}
                    />
                    Send confirmation email to customer
                  </label>
                </div>
              )}

              {/* ── RIGHT: Products & Items, Packing Slip, & Fraud ── */}
              {(!isMobile || manualModalTab !== 'form') && (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  minWidth: 0,
                  backgroundColor: '#ffffff'
                }}>
                  {/* Right Header: Tabs & Print Button */}
                  <div style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '10px',
                    background: '#ffffff',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => setManualModalTab('items')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'items' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'items' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'items' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'items' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🛒 Products &amp; Items ({manualCartItems.length})
                      </button>

                      <button
                        type="button"
                        onClick={() => setManualModalTab('slip')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'slip' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'slip' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'slip' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'slip' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🧾 Steadfast Packing Slip
                      </button>

                      <button
                        type="button"
                        onClick={() => setManualModalTab('fraud')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'fraud' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'fraud' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'fraud' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'fraud' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🛡️ Customer History &amp; Fraud
                      </button>
                    </div>

                    {manualModalTab === 'slip' && (
                      <button
                        type="button"
                        onClick={() => handlePrintPackingSlip(manualPackingSlipRef)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                          border: 'none', borderRadius: '7px', fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        🖨️ Print Slip
                      </button>
                    )}
                  </div>

                  <div style={{ padding: '18px 20px', flex: 1 }}>
                    {/* ── TAB 1: Products & Items ── */}
                    {manualModalTab === 'items' && (
                      <div className="om-edit-items-container">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                              Order Items &amp; Variants
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                              Search catalog, pick variants, adjust quantities, or customize unit prices
                            </p>
                          </div>
                          <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '3px 10px' }}>
                            {manualCartItems.length} {manualCartItems.length === 1 ? 'Item' : 'Items'}
                          </div>
                        </div>

                        {/* Items List */}
                        {manualCartItems.length === 0 ? (
                          <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b', marginBottom: '16px' }}>
                            <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>📦</div>
                            <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>No products added yet</p>
                            <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>Use the search box below to find and add products to this order.</p>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                            {manualCartItems.map((item, idx) => {
                              const attrs = item.product_attributes || {};
                              const colorOpts: any[] = attrs.colors?.options || [];
                              const sizeOpts: any[] = attrs.sizes?.options || [];
                              const weightOpts: any[] = attrs.weights?.options || [];
                              const stock = item.product_stock !== undefined ? item.product_stock : 99;
                              const subtotal = (parseFloat(item.price || 0) * parseInt(item.quantity || item.qty || 1));

                              return (
                                <div key={`manual-item-${idx}`} className="om-item-card">
                                  <div className="om-item-top">
                                    <div className="om-item-info">
                                      {item.product_image ? (
                                        <img src={item.product_image} alt="" className="om-item-thumb" />
                                      ) : (
                                        <div className="om-item-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                          📦
                                        </div>
                                      )}
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="om-item-name" title={item.product_name}>
                                          {item.product_name}
                                        </div>
                                        <div className="om-item-meta">
                                          {item.product_sku && (
                                            <span className="om-item-sku">SKU: {item.product_sku}</span>
                                          )}
                                          <span className={`om-item-stock ${stock > 5 ? 'in-stock' : stock > 0 ? 'low-stock' : 'out-stock'}`}>
                                            ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      className="om-item-delete-btn"
                                      onClick={() => handleRemoveManualItem(idx)}
                                      title="Remove product"
                                    >
                                      🗑️
                                    </button>
                                  </div>

                                  {/* Variant Selectors Row */}
                                  <div className="om-item-variants-row">
                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Color:</span>
                                      {colorOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_color || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_color', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {colorOpts.map((c: any, cIdx: number) => {
                                            const name = typeof c === 'string' ? c : c.name;
                                            return <option key={cIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. Red"
                                          value={item.selected_color || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_color', e.target.value)}
                                        />
                                      )}
                                    </div>

                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Size:</span>
                                      {sizeOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_size || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_size', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {sizeOpts.map((s: any, sIdx: number) => {
                                            const name = typeof s === 'string' ? s : s.name;
                                            return <option key={sIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. XL, 42"
                                          value={item.selected_size || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_size', e.target.value)}
                                        />
                                      )}
                                    </div>

                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Weight:</span>
                                      {weightOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_weight || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_weight', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {weightOpts.map((w: any, wIdx: number) => {
                                            const name = typeof w === 'string' ? w : w.name;
                                            return <option key={wIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. 500g, 1kg"
                                          value={item.selected_weight || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_weight', e.target.value)}
                                        />
                                      )}
                                    </div>
                                  </div>

                                  {/* Controls: Qty, Unit Price, Subtotal */}
                                  <div className="om-item-controls-row">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>Quantity:</span>
                                      <div className="om-qty-stepper">
                                        <button
                                          type="button"
                                          className="om-qty-btn"
                                          onClick={() => handleUpdateManualItemQty(idx, Math.max(1, (item.quantity || 1) - 1))}
                                        >
                                          −
                                        </button>
                                        <input
                                          type="number"
                                          min="1"
                                          className="om-qty-input"
                                          value={item.quantity || 1}
                                          onChange={(e) => handleUpdateManualItemQty(idx, parseInt(e.target.value) || 1)}
                                        />
                                        <button
                                          type="button"
                                          className="om-qty-btn"
                                          onClick={() => handleUpdateManualItemQty(idx, (item.quantity || 1) + 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>

                                    <div className="om-price-field">
                                      <span>Unit Price: ৳</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="om-price-input"
                                        value={item.price}
                                        onChange={(e) => handleUpdateManualItemPrice(idx, parseFloat(e.target.value) || 0)}
                                      />
                                    </div>

                                    <div style={{ textAlign: 'right' }}>
                                      <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block' }}>Subtotal</span>
                                      <span className="om-item-subtotal-badge">৳{subtotal.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Add Product Box */}
                        <div className="om-add-product-box">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              ➕ Add Product from Catalog
                            </span>
                            {manualProductSearching && (
                              <span style={{ fontSize: '0.75rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Loader2 size={13} className="animate-spin" /> Searching catalog...
                              </span>
                            )}
                          </div>

                          <div style={{ position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 10px' }}>
                              <Search size={15} style={{ color: '#94a3b8', marginRight: '6px' }} />
                              <input
                                type="text"
                                placeholder="Type product name or SKU to search catalog..."
                                value={manualProductSearch}
                                onChange={(e) => handleManualProductSearch(e.target.value)}
                                style={{ width: '100%', padding: '8px 0', border: 'none', outline: 'none', fontSize: '0.84rem', color: '#1e293b' }}
                              />
                              {manualProductSearch && (
                                <button
                                  type="button"
                                  onClick={() => { setManualProductSearch(''); setManualProductResults([]); }}
                                  style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* Search Dropdown Results */}
                            {manualProductResults.length > 0 && !manualPickingProduct && (
                              <div className="om-search-results-dropdown">
                                {manualProductResults.map((p) => {
                                  const price = parseFloat(p.discount_price || p.price || 0);
                                  const stock = p.stock !== undefined ? p.stock : 99;
                                  const pImg = p.image || p.images?.[0]?.image || '';
                                  const attrs = p.attributes || {};
                                  const colorOpts: any[] = (attrs.colors?.enabled && attrs.colors?.options?.length) ? attrs.colors.options : [];
                                  const sizeOpts: any[] = (attrs.sizes?.enabled && attrs.sizes?.options?.length) ? attrs.sizes.options : [];
                                  const weightOpts: any[] = (attrs.weights?.enabled && attrs.weights?.options?.length) ? attrs.weights.options : [];

                                  return (
                                    <div
                                      key={p.id}
                                      className="om-search-result-item"
                                      onClick={() => handleSelectManualProduct(p)}
                                    >
                                      {pImg ? (
                                        <img
                                          src={pImg}
                                          alt=""
                                          className="om-search-thumb"
                                          style={{
                                            width: '44px',
                                            height: '44px',
                                            minWidth: '44px',
                                            minHeight: '44px',
                                            maxWidth: '44px',
                                            maxHeight: '44px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            flexShrink: 0
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="om-search-thumb-placeholder"
                                          style={{
                                            width: '44px',
                                            height: '44px',
                                            minWidth: '44px',
                                            borderRadius: '6px',
                                            background: '#f1f5f9',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '18px',
                                            flexShrink: 0
                                          }}
                                        >
                                          📦
                                        </div>
                                      )}

                                      <div className="om-search-info">
                                        <div className="om-search-name" title={p.name_en || p.name_bn}>
                                          {p.name_en || p.name_bn}
                                        </div>
                                        <div className="om-search-sub">
                                          {p.sku && <span>SKU: {p.sku}</span>}
                                          <span style={{ color: stock > 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                                            ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                          </span>
                                        </div>

                                        {/* Available Variants Row */}
                                        {(colorOpts.length > 0 || sizeOpts.length > 0 || weightOpts.length > 0) && (
                                          <div className="om-search-variants-pills">
                                            {colorOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).join(', ')}>
                                                🎨 {colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).slice(0, 3).join(', ')}{colorOpts.length > 3 ? ` +${colorOpts.length - 3}` : ''}
                                              </span>
                                            )}
                                            {sizeOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).join(', ')}>
                                                📏 {sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).slice(0, 3).join(', ')}{sizeOpts.length > 3 ? ` +${sizeOpts.length - 3}` : ''}
                                              </span>
                                            )}
                                            {weightOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).join(', ')}>
                                                ⚖️ {weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).slice(0, 2).join(', ')}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      <div className="om-search-price-col">
                                        <div className="om-search-price">৳{price.toFixed(2)}</div>
                                        <span className="om-search-select-btn">+ Select</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Selected Product Variant Picker */}
                          {manualPickingProduct && (
                            <div className="om-product-pick-card">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  {manualPickingProduct.image || manualPickingProduct.images?.[0]?.image ? (
                                    <img src={manualPickingProduct.image || manualPickingProduct.images?.[0]?.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                                  )}
                                  <div>
                                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>{manualPickingProduct.name_en}</div>
                                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                      Catalog Base: ৳{parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setManualPickingProduct(null)}
                                  style={{ border: 'none', background: '#f1f5f9', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#64748b', cursor: 'pointer' }}
                                >
                                  ✕ Cancel
                                </button>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                                {/* Color */}
                                {manualPickingProduct.attributes?.colors?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Color</label>
                                    <select
                                      value={manualPickAttrs.color}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, color: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.colors.options || []).map((c: any, cIdx: number) => {
                                        const name = typeof c === 'string' ? c : c.name;
                                        return <option key={cIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Size */}
                                {manualPickingProduct.attributes?.sizes?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Size</label>
                                    <select
                                      value={manualPickAttrs.size}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, size: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.sizes.options || []).map((s: any, sIdx: number) => {
                                        const name = typeof s === 'string' ? s : s.name;
                                        return <option key={sIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Weight */}
                                {manualPickingProduct.attributes?.weights?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Weight</label>
                                    <select
                                      value={manualPickAttrs.weight}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, weight: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.weights.options || []).map((w: any, wIdx: number) => {
                                        const name = typeof w === 'string' ? w : w.name;
                                        return <option key={wIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Quantity */}
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Quantity</label>
                                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', width: 'fit-content' }}>
                                    <button
                                      type="button"
                                      onClick={() => setManualPickAttrs(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))}
                                      style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    >
                                      −
                                    </button>
                                    <span style={{ minWidth: '28px', textAlign: 'center', fontSize: '0.85rem', fontWeight: '700' }}>{manualPickAttrs.qty}</span>
                                    <button
                                      type="button"
                                      onClick={() => setManualPickAttrs(p => ({ ...p, qty: p.qty + 1 }))}
                                      style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                {/* Custom Unit Price */}
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Unit Price (৳)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={manualPickAttrs.price}
                                    onChange={(e) => setManualPickAttrs(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: '700' }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={handleManualAddToCart}
                                  style={{
                                    padding: '8px 18px',
                                    borderRadius: '7px',
                                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                    color: 'white',
                                    border: 'none',
                                    fontSize: '0.82rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                                  }}
                                >
                                  ✓ Add to Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── Compact Dynamic Invoice Breakdown ── */}
                        {(() => {
                          const manualTotalUnits = manualCartItems.reduce((acc, it) => acc + (parseInt(it.quantity || it.qty || 1) || 1), 0);
                          const currentDelOpt = deliveryOptions.find(o => String(o.id) === String(manualForm.delivery_option));

                          return (
                            <div className="om-invoice-summary-card">
                              <div className="om-invoice-header">
                                <div className="om-invoice-title">
                                  🧾 Order Invoice Breakdown
                                </div>
                                <span className="om-invoice-badge-pill">
                                  COD Balance Due
                                </span>
                              </div>

                              <div className="om-invoice-body">
                                {/* Items Subtotal */}
                                <div className="om-invoice-row">
                                  <span className="om-invoice-label">
                                    Items Subtotal
                                    <span className="om-invoice-sublabel">
                                      ({manualCartItems.length} {manualCartItems.length === 1 ? 'product' : 'products'} · {manualTotalUnits} {manualTotalUnits === 1 ? 'unit' : 'units'})
                                    </span>
                                  </span>
                                  <span className="om-invoice-val">
                                    ৳{manualItemsSubtotal.toFixed(2)}
                                  </span>
                                </div>

                                {/* Delivery Fee */}
                                <div className="om-invoice-row">
                                  <span className="om-invoice-label">
                                    Delivery Fee
                                    {currentDelOpt && (
                                      <span className="om-invoice-tag">
                                        🚚 {currentDelOpt.name_en || currentDelOpt.name_bn || 'Selected'}
                                      </span>
                                    )}
                                  </span>
                                  <span className="om-invoice-val">
                                    +৳{manualShipping.toFixed(2)}
                                  </span>
                                </div>

                                {/* Admin Discount */}
                                {manualAdminDiscount > 0 && (
                                  <div className="om-invoice-row discount">
                                    <span className="om-invoice-label">
                                      Admin Discount
                                      <span className="om-invoice-sublabel">
                                        ({manualForm.admin_discount_type === 'percentage' ? `${manualForm.admin_discount_value}% off` : 'Flat deduction'})
                                      </span>
                                    </span>
                                    <span className="om-invoice-val">
                                      −৳{manualAdminDiscount.toFixed(2)}
                                    </span>
                                  </div>
                                )}

                                {/* Coupon Discount */}
                                {manualCouponDiscount > 0 && (
                                  <div className="om-invoice-row discount">
                                    <span className="om-invoice-label">
                                      Coupon Discount
                                      {manualForm.coupon_code && (
                                        <span className="om-invoice-tag coupon">
                                          🏷️ {manualForm.coupon_code.toUpperCase()}
                                        </span>
                                      )}
                                    </span>
                                    <span className="om-invoice-val">
                                      −৳{manualCouponDiscount.toFixed(2)}
                                    </span>
                                  </div>
                                )}

                                {/* Total COD Payable */}
                                <div className="om-invoice-total-row">
                                  <div className="om-invoice-total-left">
                                    <span className="om-invoice-total-title">Total (COD Amount)</span>
                                    <span className="om-invoice-total-desc">Payable by customer upon delivery</span>
                                  </div>
                                  <div className="om-invoice-total-amount">
                                    ৳{manualTotal.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* ── TAB 2: Steadfast Packing Slip ── */}
                    {manualModalTab === 'slip' && (
                      <div>
                        <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                              Steadfast Packing Slip &amp; Dispatch Invoice
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                              Live auto-syncing preview of the courier packing slip for this manual order
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handlePrintPackingSlip(manualPackingSlipRef)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                              border: 'none', borderRadius: '7px', fontSize: '12px',
                              fontWeight: '600', cursor: 'pointer'
                            }}
                          >
                            🖨️ Print Packing Slip
                          </button>
                        </div>

                        {/* Printable Packing Slip Container */}
                        <div
                          ref={manualPackingSlipRef}
                          style={{
                            background: '#fff',
                            border: '1.5px solid #cbd5e1',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                            maxWidth: '780px',
                            margin: '0 auto',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {/* Slip Header */}
                          <div style={{
                            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                            color: '#fff',
                            padding: '16px 20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {siteSettings?.logo_url && (
                                <img src={siteSettings.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
                              )}
                              <div>
                                <div style={{ fontSize: '16px', fontWeight: '800', letterSpacing: '-0.02em' }}>
                                  {siteSettings?.site_name || 'Online Store'}
                                </div>
                                {siteSettings?.support_phone && (
                                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                    📞 {siteSettings.support_phone}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                Courier Logistics
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '800', color: '#60a5fa', marginTop: '2px' }}>
                                STEADFAST COURIER
                              </div>
                            </div>
                          </div>

                          {/* Info Row: Recipient & Order Meta */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ padding: '14px 18px', borderRight: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                Deliver To (Recipient)
                              </div>
                              <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                                {manualForm.customer_name || 'Customer Name'}
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563eb', marginBottom: '4px' }}>
                                📞 {manualForm.customer_phone || 'Phone Number'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#475569', lineHeight: '1.4' }}>
                                📍 {manualForm.shipping_address || 'Shipping Address'}
                              </div>
                            </div>

                            <div style={{ padding: '14px 18px', background: '#f8fafc' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                Order Info
                              </div>
                              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                                <tbody>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Type:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>Manual (Phone)</td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Date:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>{new Date().toLocaleDateString('en-GB')}</td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Status:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b', textTransform: 'capitalize' }}>
                                      {manualForm.initial_status}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Area:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>
                                      {deliveryOptions.find(o => o.id === manualForm.delivery_option)?.name_en || 'Dhaka Metro'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Items Table */}
                          <div style={{ padding: '0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>#</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Item Description</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'center' }}>Qty</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'right' }}>Price</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'right' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {manualCartItems.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                      No items added to this manual order yet.
                                    </td>
                                  </tr>
                                ) : (
                                  manualCartItems.map((item, i) => {
                                    const variantParts = [item.selected_color, item.selected_size, item.selected_weight].filter(Boolean);
                                    const price = parseFloat(item.price || 0);
                                    const qty = parseInt(item.quantity || item.qty || 1);
                                    const lineTotal = price * qty;
                                    return (
                                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 14px', color: '#64748b' }}>{i + 1}</td>
                                        <td style={{ padding: '8px 14px' }}>
                                          <div style={{ fontWeight: '700', color: '#1e293b' }}>{item.product_name}</div>
                                          {variantParts.length > 0 && (
                                            <div style={{ fontSize: '10px', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>
                                              {variantParts.join(' · ')}
                                            </div>
                                          )}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: '700', color: '#1e293b' }}>
                                          {qty}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'right', color: '#475569' }}>
                                          ৳{price.toFixed(2)}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                                          ৳{lineTotal.toFixed(2)}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Totals Summary */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <table style={{ width: '280px', borderCollapse: 'collapse', fontSize: '11px' }}>
                              <tbody>
                                <tr>
                                  <td style={{ padding: '6px 14px', color: '#64748b' }}>Items Subtotal:</td>
                                  <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                                    ৳{manualItemsSubtotal.toFixed(2)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '6px 14px', color: '#64748b' }}>Shipping Charge:</td>
                                  <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                                    ৳{manualShipping.toFixed(2)}
                                  </td>
                                </tr>
                                {manualAdminDiscount > 0 && (
                                  <tr>
                                    <td style={{ padding: '6px 14px', color: '#16a34a' }}>Admin Discount:</td>
                                    <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                                      −৳{manualAdminDiscount.toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                {manualCouponDiscount > 0 && (
                                  <tr>
                                    <td style={{ padding: '6px 14px', color: '#16a34a' }}>Coupon Discount:</td>
                                    <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                                      −৳{manualCouponDiscount.toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                <tr style={{ background: '#0f172a', color: '#fff' }}>
                                  <td style={{ padding: '10px 14px', fontWeight: '800', fontSize: '13px' }}>Total COD:</td>
                                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '800', fontSize: '15px', color: '#60a5fa' }}>
                                    ৳{manualTotal.toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Slip Footer */}
                          <div style={{ padding: '10px 18px', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>
                            Thank you for shopping with us! For questions regarding your delivery, please contact our support.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── TAB 3: Customer History & Fraud ── */}
                    {manualModalTab === 'fraud' && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                              🛡️ Customer Fraud Check &amp; Store History
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                              Cross-merchant delivery success analysis across Steadfast and Pathao networks
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const clean = (manualForm.customer_phone || '').replace(/[^0-9]/g, '');
                              if (clean) loadCustomerHistory(clean);
                            }}
                            disabled={customerHistoryLoading || !manualForm.customer_phone.trim()}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '7px 14px', backgroundColor: '#f1f5f9', color: '#334155',
                              border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '12px',
                              fontWeight: '600', cursor: 'pointer'
                            }}
                          >
                            <RefreshCw size={12} className={customerHistoryLoading ? 'animate-spin' : ''} /> Refresh Intelligence
                          </button>
                        </div>

                        {!manualForm.customer_phone.trim() ? (
                          <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                            <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>📞</div>
                            <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>Enter a Customer Phone Number</p>
                            <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>Type an 11-digit phone number in the form on the left to automatically run fraud analysis.</p>
                          </div>
                        ) : customerHistoryLoading ? (
                          <div style={{ padding: '40px', textAlign: 'center', color: '#2563eb' }}>
                            <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 10px' }} />
                            <p style={{ fontSize: '0.85rem', fontWeight: '600' }}>Querying Steadfast &amp; courier fraud database...</p>
                          </div>
                        ) : !customerHistory ? (
                          <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', color: '#64748b' }}>
                            <p style={{ fontSize: '0.88rem', fontWeight: '600' }}>No courier records found for {manualForm.customer_phone}.</p>
                            <p style={{ fontSize: '0.75rem', margin: '4px 0 0' }}>This phone number appears to be a new or first-time courier recipient.</p>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {/* Courier Cards */}
                            {['steadfast', 'pathao'].map(courier => {
                              const data = customerHistory.fraud_check?.[courier];
                              if (!data) return null;
                              const cancelRate = data.total > 0 ? Math.round(((data.cancel || 0) / data.total) * 100) : 0;
                              const deliveryRate = data.total > 0 ? Math.round(((data.delivery || 0) / data.total) * 100) : 0;
                              const isFraudRisk = data.total >= 3 && cancelRate > 50;
                              const isGood = data.total >= 2 && deliveryRate >= 80;

                              return (
                                <div
                                  key={courier}
                                  style={{
                                    border: `1.5px solid ${isFraudRisk ? '#fecaca' : isGood ? '#bbf7d0' : '#e2e8f0'}`,
                                    borderRadius: '10px',
                                    background: isFraudRisk ? '#fff5f5' : isGood ? '#f0fdf4' : '#fff',
                                    padding: '14px 18px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontWeight: '800', fontSize: '0.92rem', color: '#1e293b', textTransform: 'uppercase' }}>
                                        {courier === 'steadfast' ? 'Steadfast Courier Network' : 'Pathao Courier Network'}
                                      </span>
                                      {isFraudRisk && (
                                        <span style={{ fontSize: '0.7rem', fontWeight: '800', background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', border: '1px solid #fca5a5' }}>
                                          ⚠️ HIGH RISK
                                        </span>
                                      )}
                                      {isGood && (
                                        <span style={{ fontSize: '0.7rem', fontWeight: '800', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px', border: '1px solid #86efac' }}>
                                          ✓ TRUSTED
                                        </span>
                                      )}
                                    </div>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: deliveryRate >= 80 ? '#16a34a' : deliveryRate >= 60 ? '#2563eb' : '#dc2626' }}>
                                      {deliveryRate}% Delivery Success
                                    </span>
                                  </div>

                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', textAlign: 'center' }}>
                                    <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total Orders</div>
                                      <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e293b' }}>{data.total || 0}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#16a34a' }}>Delivered</div>
                                      <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#16a34a' }}>{data.delivery || 0}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#dc2626' }}>Cancelled/Returned</div>
                                      <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#dc2626' }}>{data.cancel || 0}</div>
                                    </div>
                                    <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                      <div style={{ fontSize: '0.7rem', color: '#d97706' }}>Return Rate</div>
                                      <div style={{ fontSize: '1.05rem', fontWeight: '800', color: cancelRate > 40 ? '#dc2626' : '#1e293b' }}>{cancelRate}%</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {/* Store History */}
                            {customerHistory.store_history && (
                              <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 18px', background: '#f8fafc' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                                  🏢 Previous Orders at Our Store
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', textAlign: 'center' }}>
                                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Store Orders</div>
                                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e293b' }}>
                                      {customerHistory.store_history.total_orders || 0}
                                    </div>
                                  </div>
                                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Total Spent</div>
                                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#2563eb' }}>
                                      ৳{parseFloat(customerHistory.store_history.total_spent || 0).toFixed(0)}
                                    </div>
                                  </div>
                                  <div style={{ background: '#fff', padding: '8px', borderRadius: '7px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Cancelled Orders</div>
                                    <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#dc2626' }}>
                                      {customerHistory.store_history.cancelled_orders || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Order Modal ─── */}
      {editModalOpen && editingOrder && (() => {
        const items: any[] = editItems;
        const itemsTotal = editItems.reduce((sum: number, it: any) => sum + (parseFloat(it.price || 0) * parseInt(it.quantity || 1)), 0);
        const shippingCost = parseFloat(editForm.shipping_cost) || 0;
        let discount = 0;
        if (discountType === 'percentage' && discountValue) {
          discount = Math.min((itemsTotal * parseFloat(discountValue)) / 100, itemsTotal);
        } else if (discountType === 'flat' && discountValue) {
          discount = Math.min(parseFloat(discountValue), itemsTotal);
        } else if (editingOrder.discount_amount) {
          discount = Math.min(parseFloat(editingOrder.discount_amount), itemsTotal);
        }
        const codAmount = Math.max(0, itemsTotal + shippingCost - discount);
        const orderDate = editingOrder.created_at
          ? new Date(editingOrder.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.65)',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              zIndex: 10000, backdropFilter: 'blur(4px)',
              padding: isMobile ? '0' : '16px'
            }}
            onClick={isMobile ? undefined : closeEditModal}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: isMobile ? '0' : '14px',
                width: '100%',
                maxWidth: isMobile ? '100%' : '1180px',
                height: isMobile ? '100dvh' : 'auto',
                maxHeight: isMobile ? '100dvh' : '95vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* ── Modal Header ── */}
              <div style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                color: 'white',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '600', color: '#f8fafc', letterSpacing: '-0.025em' }}>Edit Order</h2>
                  <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#94a3b8' }}>#{editingOrder.order_number}</p>
                </div>
                <button onClick={closeEditModal} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px', color: 'white', fontSize: '1rem',
                  width: '28px', height: '28px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s'
                }}>✕</button>
              </div>

              {/* ── Mobile Tab Bar ── */}
              {isMobile && (
                <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', flexShrink: 0 }}>
                  {([
                    { key: 'items', label: `🛒 Items (${editItems.length})` },
                    { key: 'edit',  label: '✏️ Info'  },
                    { key: 'slip',  label: '🧾 Slip'  },
                    { key: 'fraud', label: '🛡️ Fraud' },
                  ] as { key: 'items' | 'edit' | 'slip' | 'fraud'; label: string }[]).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setEditModalTab(tab.key)}
                      style={{
                        flex: 1, padding: '11px 6px',
                        border: 'none',
                        borderBottom: editModalTab === tab.key ? '2.5px solid #1a56db' : '2.5px solid transparent',
                        background: 'transparent',
                        color: editModalTab === tab.key ? '#1a56db' : '#6b7280',
                        fontWeight: editModalTab === tab.key ? '700' : '500',
                        fontSize: '13px', cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Body: Desktop = two-column | Mobile = tab-driven ── */}
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* ── LEFT: Edit Form ── */}
              {(!isMobile || editModalTab === 'edit') && (
                <div style={{
                  width: isMobile ? '100%' : '350px',
                  minWidth: isMobile ? undefined : '320px',
                  flexShrink: 0,
                  padding: '16px 18px', overflowY: 'auto',
                  borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  {/* ── Move to Category Dropdown ── */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #3b82f6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        MOVE TO CATEGORY
                      </span>
                      {(() => {
                        const cur = EDIT_STATUS_OPTIONS.find(st => st.value === editForm.status) || {
                          label: editForm.status,
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      name="status"
                      value={editForm.status}
                      onChange={handleEditFormChange}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      {EDIT_STATUS_OPTIONS.map(st => (
                        <option key={st.value} value={st.value}>
                          {st.icon} {st.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Order Platform / Source Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #8b5cf6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        ORDER PLATFORM / SOURCE
                      </span>
                      {(() => {
                        const cur = TRAFFIC_PLATFORM_OPTIONS.find(p => p.value === editForm.traffic_source) || {
                          label: editForm.traffic_source || 'Manual',
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      name="traffic_source"
                      value={editForm.traffic_source}
                      onChange={handleEditFormChange}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '8px'
                      }}
                    >
                      {TRAFFIC_PLATFORM_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Campaign / Ad Name (Optional)
                      </label>
                      <input
                        type="text"
                        name="utm_campaign"
                        value={editForm.utm_campaign}
                        onChange={handleEditFormChange}
                        placeholder="e.g. Eid Campaign, FB Ad 1, Post 2..."
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '0.82rem',
                          color: '#1e293b',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <p style={{ margin: '4px 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '4px', display: 'inline-block' }}>Order Details</p>

                  {editError && (
                    <div style={{
                      background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px'
                    }}>⚠️ {editError}</div>
                  )}

                  {/* Customer Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Customer Name</label>
                    <input type="text" name="customer_name" value={editForm.customer_name} onChange={handleEditFormChange}
                      placeholder="Customer full name"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Phone Number</label>
                    <input type="text" name="customer_phone" value={editForm.customer_phone} onChange={handleEditFormChange}
                      placeholder="Customer phone"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Shipping Address */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Shipping Address</label>
                    <textarea name="shipping_address" value={editForm.shipping_address} onChange={handleEditFormChange}
                      placeholder="Full shipping address" rows={3}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Delivery Option & Shipping Fee ── */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🚚 Delivery Option &amp; Shipping Fee
                    </p>
                    
                    {/* Delivery Option Dropdown */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                        Shipping Option
                      </label>
                      <select
                        value={editForm.delivery_option || (deliveryOptions[0]?.id || '')}
                        onChange={(e) => {
                          const optId = e.target.value ? parseInt(e.target.value) : null;
                          handleDeliveryOptionChange(optId);
                        }}
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '600',
                          backgroundColor: '#f8fafc', cursor: 'pointer', outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        {deliveryOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon || '🚚'} {opt.name_en} — ৳{parseFloat(opt.cost).toFixed(0)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Shipping Fee Input */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Shipping Cost (৳)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        name="shipping_cost"
                        value={editForm.shipping_cost}
                        onChange={(e) => setEditForm(prev => ({ ...prev, shipping_cost: e.target.value }))}
                        placeholder="Shipping fee in ৳"
                        style={{
                          width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', fontWeight: '600',
                          color: '#1e293b', outline: 'none', boxSizing: 'border-box',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: '6px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Notes</label>
                    <textarea name="notes" value={editForm.notes} onChange={handleEditFormChange}
                      placeholder="Optional order notes" rows={2}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* ── Admin Discount ── */}
                  <div style={{ marginTop: '14px', padding: '12px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      Admin Discount
                    </p>

                    {/* Current discount badge */}
                    {editingOrder.discount_amount && parseFloat(editingOrder.discount_amount) > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', padding: '7px 10px', background: '#d1fae5', borderRadius: '8px', border: '1px solid #6ee7b7' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#065f46' }}>
                          {editingOrder.admin_discount_type === 'percentage'
                            ? `−${editingOrder.admin_discount_value}% = ৳${parseFloat(editingOrder.discount_amount).toFixed(2)}`
                            : `−৳${parseFloat(editingOrder.discount_amount).toFixed(2)} (flat)`
                          }
                        </span>
                        <button
                          onClick={() => handleApplyDiscount(true)}
                          disabled={discountSaving}
                          title="Remove discount"
                          style={{ border: 'none', background: '#fca5a5', color: '#991b1b', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                        >✕ Remove</button>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                      {/* Type selector */}
                      <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Type</label>
                        <select
                          value={discountType}
                          onChange={(e) => { setDiscountType(e.target.value as 'flat' | 'percentage' | ''); setDiscountError(''); }}
                          style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '12px', color: '#111827', background: 'white', cursor: 'pointer', outline: 'none' }}
                        >
                          <option value="">— Type —</option>
                          <option value="flat">৳ Flat</option>
                          <option value="percentage">% Percent</option>
                        </select>
                      </div>

                      {/* Value input */}
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                          Value {discountType === 'percentage' ? '(%)' : discountType === 'flat' ? '(৳)' : ''}
                        </label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={discountValue}
                          onChange={(e) => { setDiscountValue(e.target.value); setDiscountError(''); }}
                          placeholder={discountType === 'percentage' ? 'e.g. 10' : discountType === 'flat' ? 'e.g. 150' : ''}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '13px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Apply button */}
                      <button
                        onClick={() => handleApplyDiscount(false)}
                        disabled={discountSaving || !discountType || !discountValue}
                        style={{
                          padding: '7px 12px',
                          border: 'none',
                          borderRadius: '7px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: discountSaving || !discountType || !discountValue ? 'not-allowed' : 'pointer',
                          background: discountSaving || !discountType || !discountValue ? '#e5e7eb' : 'linear-gradient(135deg, #16a34a, #15803d)',
                          color: discountSaving || !discountType || !discountValue ? '#9ca3af' : '#fff',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                          alignSelf: 'flex-end',
                          boxSizing: 'border-box',
                          height: '34px',
                        }}
                      >
                        {discountSaving ? '⏳' : '✓ Apply'}
                      </button>
                    </div>

                    {discountError && (
                      <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '11px' }}>
                        ⚠️ {discountError}
                      </div>
                    )}

                    {/* Live preview */}
                    {(() => {
                      const items = editingOrder.items || [];
                      const itemsSubtotal = items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || 0), 0);
                      const shipping = parseFloat(editForm.shipping_cost) || 0;
                      const currentDiscount = parseFloat(editingOrder.discount_amount || 0);
                      const finalAmt = itemsSubtotal + shipping - currentDiscount;
                      return (
                        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '11px', color: '#374151' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Items Subtotal</span><span style={{ fontWeight: '600' }}>৳{itemsSubtotal.toFixed(2)}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}><span>Shipping</span><span style={{ fontWeight: '600' }}>৳{shipping.toFixed(2)}</span></div>
                          {currentDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', color: '#16a34a' }}><span>Discount</span><span style={{ fontWeight: '700' }}>−৳{currentDiscount.toFixed(2)}</span></div>}
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #bbf7d0', paddingTop: '4px', marginTop: '2px' }}><span style={{ fontWeight: '800', color: '#065f46' }}>COD (Final)</span><span style={{ fontWeight: '800', color: '#065f46', fontSize: '12px' }}>৳{finalAmt.toFixed(2)}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )} {/* end LEFT panel */}

              {/* ── RIGHT: Products & Items, Packing Slip, & Fraud ── */}
              {(!isMobile || editModalTab !== 'edit') && (
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px', backgroundColor: '#fff' }}>
                  {/* Desktop Right Column Tabs */}
                  {!isMobile && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #e2e8f0',
                      paddingBottom: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => setEditModalTab('items')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'items' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'items' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'items' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'items' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🛒 Products &amp; Items
                          <span style={{
                            background: editModalTab === 'items' ? '#2563eb' : '#cbd5e1',
                            color: editModalTab === 'items' ? '#fff' : '#475569',
                            borderRadius: '10px',
                            padding: '1px 7px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}>{editItems.length}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setEditModalTab('slip')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'slip' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'slip' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'slip' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'slip' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🧾 Steadfast Packing Slip
                        </button>

                        <button
                          type="button"
                          onClick={() => setEditModalTab('fraud')}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: editModalTab === 'fraud' ? '1px solid #bfdbfe' : '1px solid transparent',
                            background: editModalTab === 'fraud' ? '#eff6ff' : '#f8fafc',
                            color: editModalTab === 'fraud' ? '#1d4ed8' : '#64748b',
                            fontWeight: editModalTab === 'fraud' ? '700' : '600',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.15s'
                          }}
                        >
                          🛡️ Customer History &amp; Fraud
                        </button>
                      </div>

                      {editModalTab === 'slip' && (
                        <button type="button" onClick={handlePrintPackingSlip} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                          border: 'none', borderRadius: '7px', fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer'
                        }}>🖨️ Print Slip</button>
                      )}
                    </div>
                  )}

                  {/* ── TAB 1: Products & Variants Editor ── */}
                  {editModalTab === 'items' && (
                    <div className="om-edit-items-container">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                            Order Items &amp; Variants
                          </h4>
                          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                            Modify variants, adjust quantities, change prices, or add additional products
                          </p>
                        </div>
                        <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '3px 10px' }}>
                          {editItems.length} {editItems.length === 1 ? 'Item' : 'Items'}
                        </div>
                      </div>

                      {/* Items List */}
                      {editItems.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                          <p style={{ fontSize: '0.9rem', fontWeight: '600', margin: '0 0 6px' }}>No products in this order</p>
                          <p style={{ fontSize: '0.78rem', margin: 0 }}>Use the search box below to add products.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {editItems.map((item, idx) => {
                            const attrs = item.product_attributes || {};
                            const colorOpts: any[] = attrs.colors?.options || [];
                            const sizeOpts: any[] = attrs.sizes?.options || [];
                            const weightOpts: any[] = attrs.weights?.options || [];
                            const stock = item.product_stock !== undefined ? item.product_stock : 99;
                            const subtotal = (parseFloat(item.price || 0) * parseInt(item.quantity || 1));

                            return (
                              <div key={item.id || `new-item-${idx}`} className="om-item-card">
                                <div className="om-item-top">
                                  <div className="om-item-info">
                                    {item.product_image ? (
                                      <img src={item.product_image} alt="" className="om-item-thumb" />
                                    ) : (
                                      <div className="om-item-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                        📦
                                      </div>
                                    )}
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div className="om-item-name" title={item.product_name}>
                                        {item.product_name}
                                      </div>
                                      <div className="om-item-meta">
                                        {item.product_sku && (
                                          <span className="om-item-sku">SKU: {item.product_sku}</span>
                                        )}
                                        <span className={`om-item-stock ${stock > 5 ? 'in-stock' : stock > 0 ? 'low-stock' : 'out-stock'}`}>
                                          ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEditItem(idx)}
                                    className="om-remove-btn"
                                    title="Remove this product"
                                    disabled={editItems.length <= 1}
                                    style={{ opacity: editItems.length <= 1 ? 0.4 : 1, cursor: editItems.length <= 1 ? 'not-allowed' : 'pointer' }}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>

                                {/* Variants Row */}
                                <div className="om-item-variants-row">
                                  {/* Color */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Color:</span>
                                    {colorOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_color || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_color', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_color && !colorOpts.some((c: any) => (typeof c === 'string' ? c : c.name) === item.selected_color) && (
                                          <option value={item.selected_color}>{item.selected_color} (current)</option>
                                        )}
                                        {colorOpts.map((c: any, cIdx: number) => {
                                          const name = typeof c === 'string' ? c : c.name;
                                          const extra = typeof c === 'object' && c.extra_price ? ` (+৳${c.extra_price})` : '';
                                          return <option key={cIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. Red, Black"
                                        value={item.selected_color || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_color', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '90px' }}
                                      />
                                    )}
                                  </div>

                                  {/* Size */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Size:</span>
                                    {sizeOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_size || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_size', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_size && !sizeOpts.some((s: any) => (typeof s === 'string' ? s : s.name) === item.selected_size) && (
                                          <option value={item.selected_size}>{item.selected_size} (current)</option>
                                        )}
                                        {sizeOpts.map((s: any, sIdx: number) => {
                                          const name = typeof s === 'string' ? s : s.name;
                                          const extra = typeof s === 'object' && s.extra_price ? ` (+৳${s.extra_price})` : '';
                                          return <option key={sIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. M, L, XL"
                                        value={item.selected_size || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_size', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '80px' }}
                                      />
                                    )}
                                  </div>

                                  {/* Weight */}
                                  <div className="om-variant-field">
                                    <span className="om-variant-label">Weight:</span>
                                    {weightOpts.length > 0 ? (
                                      <select
                                        className="om-variant-select"
                                        value={item.selected_weight || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_weight', e.target.value)}
                                      >
                                        <option value="">— None —</option>
                                        {item.selected_weight && !weightOpts.some((w: any) => (typeof w === 'string' ? w : w.name) === item.selected_weight) && (
                                          <option value={item.selected_weight}>{item.selected_weight} (current)</option>
                                        )}
                                        {weightOpts.map((w: any, wIdx: number) => {
                                          const name = typeof w === 'string' ? w : w.name;
                                          const extra = typeof w === 'object' && w.extra_price ? ` (+৳${w.extra_price})` : '';
                                          return <option key={wIdx} value={name}>{name}{extra}</option>;
                                        })}
                                      </select>
                                    ) : (
                                      <input
                                        type="text"
                                        placeholder="e.g. 500g, 1kg"
                                        value={item.selected_weight || ''}
                                        onChange={(e) => handleUpdateItemVariant(idx, 'selected_weight', e.target.value)}
                                        style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem', width: '80px' }}
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Controls: Qty, Unit Price, Subtotal */}
                                <div className="om-item-controls-row">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>Quantity:</span>
                                    <div className="om-qty-stepper">
                                      <button
                                        type="button"
                                        className="om-qty-btn"
                                        onClick={() => handleUpdateItemQty(idx, Math.max(1, item.quantity - 1))}
                                      >
                                        −
                                      </button>
                                      <input
                                        type="number"
                                        min="1"
                                        className="om-qty-input"
                                        value={item.quantity}
                                        onChange={(e) => handleUpdateItemQty(idx, parseInt(e.target.value) || 1)}
                                      />
                                      <button
                                        type="button"
                                        className="om-qty-btn"
                                        onClick={() => handleUpdateItemQty(idx, item.quantity + 1)}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>

                                  <div className="om-price-field">
                                    <span>Unit Price: ৳</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      className="om-price-input"
                                      value={item.price}
                                      onChange={(e) => handleUpdateItemPrice(idx, parseFloat(e.target.value) || 0)}
                                    />
                                  </div>

                                  <div style={{ textAlign: 'right' }}>
                                    <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block' }}>Subtotal</span>
                                    <span className="om-item-subtotal-badge">৳{subtotal.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add Product Box */}
                      <div className="om-add-product-box">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            ➕ Add Additional Product to Order
                          </span>
                          {editProductSearching && (
                            <span style={{ fontSize: '0.75rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Loader2 size={13} className="animate-spin" /> Searching catalog...
                            </span>
                          )}
                        </div>

                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 10px' }}>
                            <Search size={15} style={{ color: '#94a3b8', marginRight: '6px' }} />
                            <input
                              type="text"
                              placeholder="Type product name or SKU to search catalog..."
                              value={editProductSearch}
                              onChange={(e) => handleEditProductSearch(e.target.value)}
                              style={{ width: '100%', padding: '8px 0', border: 'none', outline: 'none', fontSize: '0.84rem', color: '#1e293b' }}
                            />
                            {editProductSearch && (
                              <button
                                type="button"
                                onClick={() => { setEditProductSearch(''); setEditProductResults([]); }}
                                style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }}
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {/* Search Results Dropdown */}
                          {editProductResults.length > 0 && !editPickingProduct && (
                            <div className="om-search-results-dropdown">
                              {editProductResults.map((p) => {
                                const pPrice = parseFloat(p.discount_price || p.price || 0);
                                const pImg = p.image || p.images?.[0]?.image || '';
                                const attrs = p.attributes || {};
                                const colorOpts: any[] = (attrs.colors?.enabled && attrs.colors?.options?.length) ? attrs.colors.options : [];
                                const sizeOpts: any[] = (attrs.sizes?.enabled && attrs.sizes?.options?.length) ? attrs.sizes.options : [];
                                const weightOpts: any[] = (attrs.weights?.enabled && attrs.weights?.options?.length) ? attrs.weights.options : [];

                                return (
                                  <div
                                    key={p.id}
                                    className="om-search-result-item"
                                    onClick={() => handleSelectProductToEdit(p)}
                                  >
                                    {pImg ? (
                                      <img
                                        src={pImg}
                                        alt=""
                                        className="om-search-thumb"
                                        style={{
                                          width: '44px',
                                          height: '44px',
                                          minWidth: '44px',
                                          minHeight: '44px',
                                          maxWidth: '44px',
                                          maxHeight: '44px',
                                          objectFit: 'cover',
                                          borderRadius: '6px',
                                          border: '1px solid #e2e8f0',
                                          flexShrink: 0
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className="om-search-thumb-placeholder"
                                        style={{
                                          width: '44px',
                                          height: '44px',
                                          minWidth: '44px',
                                          borderRadius: '6px',
                                          background: '#f1f5f9',
                                          border: '1px solid #e2e8f0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '18px',
                                          flexShrink: 0
                                        }}
                                      >
                                        📦
                                      </div>
                                    )}

                                    <div className="om-search-info">
                                      <div className="om-search-name" title={p.name_en || p.name_bn}>
                                        {p.name_en || p.name_bn}
                                      </div>
                                      <div className="om-search-sub">
                                        {p.sku && <span>SKU: {p.sku}</span>}
                                        <span style={{ color: p.stock > 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                                          ● {p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}
                                        </span>
                                      </div>

                                      {/* Available Variants Row */}
                                      {(colorOpts.length > 0 || sizeOpts.length > 0 || weightOpts.length > 0) && (
                                        <div className="om-search-variants-pills">
                                          {colorOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).join(', ')}>
                                              🎨 {colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).slice(0, 3).join(', ')}{colorOpts.length > 3 ? ` +${colorOpts.length - 3}` : ''}
                                            </span>
                                          )}
                                          {sizeOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).join(', ')}>
                                              📏 {sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).slice(0, 3).join(', ')}{sizeOpts.length > 3 ? ` +${sizeOpts.length - 3}` : ''}
                                            </span>
                                          )}
                                          {weightOpts.length > 0 && (
                                            <span className="om-search-variant-pill" title={weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).join(', ')}>
                                              ⚖️ {weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).slice(0, 2).join(', ')}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div className="om-search-price-col">
                                      <div className="om-search-price">৳{pPrice.toFixed(2)}</div>
                                      <span className="om-search-select-btn">+ Select</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Picking Product Variant Selector */}
                        {editPickingProduct && (
                          <div style={{ marginTop: '10px', background: 'white', border: '1px solid #93c5fd', borderRadius: '8px', padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                              {editPickingProduct.image && (
                                <img src={editPickingProduct.image} alt="" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>{editPickingProduct.name_en}</div>
                                <div style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: '600' }}>
                                  Catalog Price: ৳{parseFloat(editPickingProduct.discount_price || editPickingProduct.price).toFixed(2)}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setEditPickingProduct(null)}
                                style={{ border: 'none', background: '#f1f5f9', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#64748b', cursor: 'pointer' }}
                              >
                                ✕ Cancel
                              </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                              {/* Color */}
                              {editPickingProduct.attributes?.colors?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Color</label>
                                  <select
                                    value={editPickAttrs.color}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, color: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.colors.options || []).map((c: any, cIdx: number) => {
                                      const name = typeof c === 'string' ? c : c.name;
                                      return <option key={cIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Size */}
                              {editPickingProduct.attributes?.sizes?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Size</label>
                                  <select
                                    value={editPickAttrs.size}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, size: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.sizes.options || []).map((s: any, sIdx: number) => {
                                      const name = typeof s === 'string' ? s : s.name;
                                      return <option key={sIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Weight */}
                              {editPickingProduct.attributes?.weights?.enabled && (
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Weight</label>
                                  <select
                                    value={editPickAttrs.weight}
                                    onChange={(e) => setEditPickAttrs(p => ({ ...p, weight: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                  >
                                    <option value="">— None —</option>
                                    {(editPickingProduct.attributes.weights.options || []).map((w: any, wIdx: number) => {
                                      const name = typeof w === 'string' ? w : w.name;
                                      return <option key={wIdx} value={name}>{name}</option>;
                                    })}
                                  </select>
                                </div>
                              )}

                              {/* Quantity */}
                              <div>
                                <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Quantity</label>
                                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', width: 'fit-content' }}>
                                  <button
                                    type="button"
                                    onClick={() => setEditPickAttrs(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))}
                                    style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                  >
                                    −
                                  </button>
                                  <span style={{ minWidth: '28px', textAlign: 'center', fontSize: '0.85rem', fontWeight: '700' }}>{editPickAttrs.qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => setEditPickAttrs(p => ({ ...p, qty: p.qty + 1 }))}
                                    style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>

                              {/* Custom Unit Price */}
                              <div>
                                <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Unit Price (৳)</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={editPickAttrs.price}
                                  onChange={(e) => setEditPickAttrs(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                                  style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: '700' }}
                                />
                              </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={handleAddProductToEditItems}
                                style={{
                                  padding: '8px 18px',
                                  borderRadius: '7px',
                                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                  color: 'white',
                                  border: 'none',
                                  fontSize: '0.82rem',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                                }}
                              >
                                ✓ Add to Order
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ── Compact Dynamic Invoice Breakdown ── */}
                      {(() => {
                        const editTotalUnits = editItems.reduce((acc, it) => acc + (parseInt(it.quantity || it.qty || 1) || 1), 0);
                        const curDelOpt = deliveryOptions.find(o => String(o.id) === String(editForm.delivery_option));

                        return (
                          <div className="om-invoice-summary-card">
                            <div className="om-invoice-header">
                              <div className="om-invoice-title">
                                🧾 Order Invoice Breakdown
                              </div>
                              <span className="om-invoice-badge-pill">
                                COD Balance Due
                              </span>
                            </div>

                            <div className="om-invoice-body">
                              <div className="om-invoice-row">
                                <span className="om-invoice-label">
                                  Items Subtotal
                                  <span className="om-invoice-sublabel">
                                    ({editItems.length} {editItems.length === 1 ? 'product' : 'products'} · {editTotalUnits} {editTotalUnits === 1 ? 'unit' : 'units'})
                                  </span>
                                </span>
                                <span className="om-invoice-val">
                                  ৳{itemsTotal.toFixed(2)}
                                </span>
                              </div>

                              <div className="om-invoice-row">
                                <span className="om-invoice-label">
                                  Delivery Fee
                                  {curDelOpt && (
                                    <span className="om-invoice-tag">
                                      🚚 {curDelOpt.name_en || curDelOpt.name_bn || 'Selected'}
                                    </span>
                                  )}
                                </span>
                                <span className="om-invoice-val">
                                  +৳{shippingCost.toFixed(2)}
                                </span>
                              </div>

                              {discount > 0 && (
                                <div className="om-invoice-row discount">
                                  <span className="om-invoice-label">
                                    Admin Discount
                                    <span className="om-invoice-sublabel">
                                      ({editingOrder?.admin_discount_type === 'percentage' ? `${editingOrder?.admin_discount_value}% off` : 'Flat deduction'})
                                    </span>
                                  </span>
                                  <span className="om-invoice-val">
                                    −৳{discount.toFixed(2)}
                                  </span>
                                </div>
                              )}

                              <div className="om-invoice-total-row">
                                <div className="om-invoice-total-left">
                                  <span className="om-invoice-total-title">Total (COD Amount)</span>
                                  <span className="om-invoice-total-desc">Payable by customer upon delivery</span>
                                </div>
                                <div className="om-invoice-total-amount">
                                  ৳{codAmount.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* ── TAB 2: Steadfast Packing Slip ── */}
                  {editModalTab === 'slip' && (
                    <div>
                      {isMobile && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Packing Slip</span>
                          <button type="button" onClick={handlePrintPackingSlip} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 12px', backgroundColor: '#0f172a', color: 'white',
                            border: 'none', borderRadius: '6px', fontSize: '12px',
                            fontWeight: '600', cursor: 'pointer'
                          }}>🖨️ Print</button>
                        </div>
                      )}

                      {/* ── Slip Content (ref for print) ── */}
                      <div ref={packingSlipRef} style={{
                    border: '1px solid #d1d5db', borderRadius: '8px',
                    overflow: 'hidden', fontSize: '13px', color: '#111', fontFamily: 'Arial, sans-serif'
                  }}>

                    {/* Slip Header */}
                    <div style={{
                      padding: '14px 18px',
                      background: '#111827',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {siteSettings.logo_url && (
                          <img src={siteSettings.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '15px', color: '#fff' }}>{siteSettings.site_name || 'Store'}</div>
                          {siteSettings.support_phone && (
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>📞 {siteSettings.support_phone}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Packing Slip</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#fff', marginTop: '2px' }}>STEADFAST COURIER</div>
                      </div>
                    </div>

                    {/* Invoice + Recipient row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                      {/* Ship To */}
                      <div style={{ flex: 1, padding: '13px 16px', borderRight: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Ship To</div>
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '3px' }}>{editForm.customer_name || '—'}</div>
                        <div style={{ color: '#374151', marginBottom: '3px' }}>📞 {editForm.customer_phone || '—'}</div>
                        <div style={{ color: '#374151', lineHeight: '1.5' }}>📍 {editForm.shipping_address || '—'}</div>
                        {editForm.notes && (
                          <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>Note: {editForm.notes}</div>
                        )}
                      </div>
                      {/* Order Info */}
                      <div style={{ width: '210px', flexShrink: 0, padding: '13px 16px', backgroundColor: '#f9fafb' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Order Info</div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <tbody>
                            {([
                              ['Invoice', editingOrder.order_number],
                              ['Date', orderDate],
                              ['Status', editForm.status.charAt(0).toUpperCase() + editForm.status.slice(1)],
                              ...(editingOrder.steadfast_tracking_code ? [['Tracking', editingOrder.steadfast_tracking_code]] : []),
                              ...(editingOrder.payment_method ? [['Payment', editingOrder.payment_method === 'cash_on_delivery' ? 'COD' : editingOrder.payment_method]] : []),
                            ] as [string, string][]).map(([k, v]) => (
                              <tr key={k}>
                                <td style={{ padding: '3px 0', color: '#6b7280', fontWeight: '600', paddingRight: '8px', whiteSpace: 'nowrap' }}>{k}</td>
                                <td style={{ padding: '3px 0', fontWeight: '700', wordBreak: 'break-all' }}>{v}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Items Table */}
                    <div style={{ padding: '0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Product</th>
                            <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Variants</th>
                            <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Unit Price</th>
                            <th style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
                          ) : items.map((item: any, idx: number) => {
                            const color = item.selected_color || item.color || '';
                            const size = item.selected_size || item.size || '';
                            const weight = item.selected_weight || item.weight || '';
                            return (
                              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                                <td style={{ padding: '9px 14px', color: '#6b7280' }}>{idx + 1}</td>
                                <td style={{ padding: '9px 14px', fontWeight: '600' }}>{item.product_name || item.name || 'Product'}</td>
                                <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '12px' }}>
                                  {(color || size || weight) ? (
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                      {color && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                          {color}
                                        </span>
                                      )}
                                      {size && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {size}
                                        </span>
                                      )}
                                      {weight && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                          {weight}
                                        </span>
                                      )}
                                    </div>
                                  ) : '—'}
                                </td>
                                <td style={{ padding: '9px 14px', textAlign: 'center' }}>{item.quantity}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right' }}>৳{parseFloat(item.price || 0).toFixed(2)}</td>
                                <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '600' }}>৳{parseFloat(item.subtotal || 0).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div style={{ borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ width: '260px', borderCollapse: 'collapse', fontSize: '13px', margin: '0' }}>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Items Subtotal</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{itemsTotal.toFixed(2)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 16px', color: '#6b7280' }}>Shipping</td>
                            <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600' }}>৳{shippingCost.toFixed(2)}</td>
                          </tr>
                          {discount > 0 && (
                            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px 16px', color: '#16a34a' }}>Discount</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>-৳{discount.toFixed(2)}</td>
                            </tr>
                          )}
                          <tr style={{ backgroundColor: '#111827' }}>
                            <td style={{ padding: '10px 16px', color: 'white', fontWeight: '800', fontSize: '14px' }}>COD Amount</td>
                            <td style={{ padding: '10px 16px', textAlign: 'right', color: 'white', fontWeight: '800', fontSize: '15px' }}>৳{codAmount.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Footer note */}
                    <div style={{ padding: '10px 16px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                      This is a system-generated packing slip for Steadfast Courier. Please attach to the parcel before dispatch.
                    </div>
                  </div>
                  </div>
                  )} {/* end slip tab */}

                  {/* ── Customer Fraud Check (Cross-Merchant) ── */}
                  {editModalTab === 'fraud' && (
                  <div style={{ marginTop: isMobile ? '0' : '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <p style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                        🛡️ Customer Fraud Check
                      </p>
                      <button
                        onClick={() => editingOrder?.customer_phone && loadCustomerHistory(editingOrder.customer_phone)}
                        disabled={customerHistoryLoading}
                        title="Refresh fraud check"
                        style={{
                          border: 'none',
                          background: customerHistoryLoading ? '#e5e7eb' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: customerHistoryLoading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                        }}
                        onMouseEnter={(e) => !customerHistoryLoading && (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        {customerHistoryLoading ? '⏳' : '🔄'} Refresh
                      </button>
                    </div>

                    {customerHistoryLoading && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                        ⏳ Checking customer across courier networks...
                      </div>
                    )}

                    {customerHistoryError && (
                      <div style={{ padding: '12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '8px', fontSize: '12px', marginBottom: '10px' }}>
                        ⚠️ {customerHistoryError}
                      </div>
                    )}

                    {!customerHistoryLoading && !customerHistoryError && !customerHistory && (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
                        No fraud check data available yet.
                      </div>
                    )}

                    {!customerHistoryLoading && customerHistory && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Courier Fraud Stats Cards */}
                        {['steadfast', 'pathao'].map((courier) => {
                          const data = customerHistory.fraud_check?.[courier];
                          if (!data) return null;
                          const hasError = !!data.error;
                          const isConfigError = hasError && data.error.includes('not configured');
                          const courierLabel = courier === 'steadfast' ? 'Steadfast' : 'Pathao';
                          const courierIcon = courier === 'steadfast' ? '🚚' : '📦';
                          const courierColor = courier === 'steadfast' ? '#6c3fbf' : '#e85d04';

                          if (isConfigError) {
                            return (
                              <div key={courier} style={{ border: '1px dashed #d1d5db', borderRadius: '8px', padding: '10px 14px', background: '#fafafa' }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', marginBottom: '2px' }}>
                                  {courierIcon} {courierLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>Not configured — add login credentials in Integration Settings</div>
                              </div>
                            );
                          }

                          if (hasError) {
                            const isRateLimited = data.rate_limited;
                            return (
                              <div key={courier} style={{
                                border: `1px solid ${isRateLimited ? '#fde68a' : '#fecaca'}`,
                                borderRadius: '8px',
                                padding: '10px 14px',
                                background: isRateLimited ? '#fffbeb' : '#fef2f2',
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: '700', color: courierColor, marginBottom: '4px' }}>
                                  {courierIcon} {courierLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: isRateLimited ? '#92400e' : '#b91c1c', lineHeight: '1.4' }}>
                                  {isRateLimited ? '⏳' : '⚠️'} {data.error}
                                </div>
                                {isRateLimited && (
                                  <div style={{ fontSize: '10px', color: '#b45309', marginTop: '4px', fontStyle: 'italic' }}>
                                    💡 Results are cached — previously checked numbers won't use quota
                                  </div>
                                )}
                              </div>
                            );
                          }

                          const successRate = data.total > 0 ? Math.round((data.success / data.total) * 100) : 0;
                          const cancelRate = data.total > 0 ? Math.round((data.cancel / data.total) * 100) : 0;
                          const isFraudRisk = data.total >= 3 && cancelRate > 50;
                          const isGoodCustomer = data.total >= 3 && successRate >= 70;
                          const isNewCustomer = data.total === 0;

                          return (
                            <div key={courier} style={{
                              border: `1px solid ${isFraudRisk ? '#fecaca' : isGoodCustomer ? '#bbf7d0' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              background: isFraudRisk ? '#fff5f5' : isGoodCustomer ? '#f0fdf4' : '#fff',
                            }}>
                              {/* Courier Header */}
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 14px',
                                background: isFraudRisk ? '#fef2f2' : isGoodCustomer ? '#f0fdf4' : '#f8fafc',
                                borderBottom: `1px solid ${isFraudRisk ? '#fecaca' : isGoodCustomer ? '#bbf7d0' : '#e5e7eb'}`,
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '14px' }}>{courierIcon}</span>
                                  <span style={{ fontSize: '12px', fontWeight: '800', color: courierColor }}>{courierLabel}</span>
                                </div>
                                {isFraudRisk && (
                                  <span style={{ background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ⚠️ FRAUD RISK
                                  </span>
                                )}
                                {isGoodCustomer && (
                                  <span style={{ background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ✅ TRUSTED
                                  </span>
                                )}
                                {isNewCustomer && (
                                  <span style={{ background: '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    🆕 NEW CUSTOMER
                                  </span>
                                )}
                                {!isFraudRisk && !isGoodCustomer && !isNewCustomer && (
                                  <span style={{ background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                    ⚡ MODERATE
                                  </span>
                                )}
                              </div>

                              {/* Stats Row */}
                              <div style={{ display: 'flex', gap: '0' }}>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#059669' }}>{data.success}</div>
                                  <div style={{ fontSize: '9px', color: '#059669', fontWeight: '700', textTransform: 'uppercase' }}>Delivered</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#dc2626' }}>{data.cancel}</div>
                                  <div style={{ fontSize: '9px', color: '#dc2626', fontWeight: '700', textTransform: 'uppercase' }}>Cancelled</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: '#111827' }}>{data.total}</div>
                                  <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>Total</div>
                                </div>
                                <div style={{ flex: 1, padding: '10px 8px', textAlign: 'center' }}>
                                  <div style={{ fontSize: '20px', fontWeight: '900', color: successRate >= 70 ? '#059669' : successRate >= 40 ? '#f59e0b' : '#dc2626' }}>
                                    {data.total > 0 ? `${successRate}%` : '—'}
                                  </div>
                                  <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>Success Rate</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Our Store Orders for this customer */}
                        {customerHistory.store_orders && customerHistory.store_orders.length > 0 && (
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>
                                🏪 Our Store Orders ({customerHistory.store_order_count})
                              </span>
                            </div>
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerHistory.store_orders.map((rec: any, idx: number) => {
                                    const statusColors: Record<string, { bg: string; color: string; label: string }> = {
                                      delivered: { bg: '#d1fae5', color: '#065f46', label: 'Delivered' },
                                      shipped: { bg: '#dbeafe', color: '#1e40af', label: 'Shipped' },
                                      processing: { bg: '#fef3c7', color: '#92400e', label: 'Processing' },
                                      pending: { bg: '#f3f4f6', color: '#374151', label: 'Pending' },
                                      cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
                                    };
                                    const st = statusColors[rec.status] || { bg: '#f3f4f6', color: '#374151', label: rec.status || 'Unknown' };
                                    const isCurrentOrder = rec.order_number === editingOrder?.order_number;
                                    return (
                                      <tr key={idx} style={{
                                        borderBottom: '1px solid #f3f4f6',
                                        background: isCurrentOrder ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                                      }}>
                                        <td style={{ padding: '6px 10px', fontWeight: isCurrentOrder ? '700' : '500', color: isCurrentOrder ? '#1d4ed8' : '#111827', fontSize: '11px' }}>
                                          {rec.order_number || '—'}
                                          {isCurrentOrder && <span style={{ fontSize: '8px', color: '#2563eb', marginLeft: '3px' }}>(this)</span>}
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>৳{parseFloat(rec.total_amount || 0).toLocaleString()}</td>
                                        <td style={{ padding: '6px 10px' }}>
                                          <span style={{ background: st.bg, color: st.color, borderRadius: '10px', padding: '1px 7px', fontSize: '9px', fontWeight: '700', whiteSpace: 'nowrap' }}>{st.label}</span>
                                        </td>
                                        <td style={{ padding: '6px 10px', color: '#6b7280', fontSize: '10px', whiteSpace: 'nowrap' }}>
                                          {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  )} {/* end fraud tab */}
                </div>
              )} {/* end RIGHT panel */}
              </div>

              {/* ── Footer ── */}
              <div style={{
                padding: isMobile ? '12px 16px' : '12px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                gap: '10px',
                backgroundColor: '#f8fafc', flexShrink: 0
              }}>
                <button onClick={closeEditModal} disabled={editSaving} style={{
                  flex: isMobile ? 1 : undefined,
                  padding: '9px 20px', backgroundColor: '#e2e8f0',
                  color: '#475569', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
                >Cancel</button>
                <button onClick={handleSaveEdit} disabled={editSaving} style={{
                  flex: isMobile ? 2 : undefined,
                  padding: '9px 24px',
                  background: editSaving ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '0.85rem', fontWeight: '600',
                  cursor: editSaving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  boxShadow: editSaving ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.2)',
                  transition: 'all 0.2s'
                }}>
                  {editSaving ? <>Saving...</> : <>Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Order View Modal */}
      {viewModalOpen && selectedOrder && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeViewModal}
        >
          <div 
            className="modal-content" 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              animation: 'modalSlideIn 0.3s ease-out',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Fixed */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', letterSpacing: '-0.025em', color: '#f8fafc' }}>Order Details</h2>
                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>Order #{selectedOrder.order_number}</p>
              </div>
              <button 
                onClick={closeViewModal}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '1.1rem',
                  width: '28px',
                  height: '28px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                ✕
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div 
              className="modal-body-scroll"
              style={{ 
                padding: '20px 24px',
                overflowY: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
              {/* Top two-column summary bar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                {[
                  { label: 'Order Date', value: selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A' },
                  { 
                    label: 'Platform / Source', 
                    value: (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                        {renderTrafficBadge(selectedOrder)}
                      </div>
                    )
                  },
                  { label: 'Payment', value: selectedOrder.payment_method === 'cash_on_delivery' ? 'Cash on Delivery' : (selectedOrder.payment_method || 'COD') },
                  { label: 'Last Updated', value: selectedOrder.updated_at ? new Date(selectedOrder.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A' },
                ].map(card => (
                  <div key={card.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{card.label}</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1e293b' }}>{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Customer Information */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Customer Information
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <tbody>
                      {[
                        { label: 'Full Name', value: selectedOrder.customer_name },
                        { label: 'Phone', value: selectedOrder.customer_phone || 'N/A' },
                        { label: 'Shipping Address', value: selectedOrder.shipping_address },
                        { label: 'Delivery Option', value: (
                          <span>
                            <strong>{selectedOrder.delivery_option_name || (selectedOrder.is_inside_dhaka ? 'Inside Dhaka' : (parseFloat(selectedOrder.shipping_cost || 0) > 60 ? 'Outside Dhaka' : 'Standard Delivery'))}</strong>
                            <span style={{ color: '#2563eb', fontWeight: '600', marginLeft: '8px' }}>(৳{parseFloat(selectedOrder.shipping_cost || 0).toFixed(2)})</span>
                          </span>
                        )},
                        { label: 'Order Status', value: (
                          <span className={`status-badge ${selectedOrder.status?.toLowerCase() || 'pending'}`} style={{ display: 'inline-block', fontSize: '0.72rem', padding: '3px 10px' }}>
                            {(selectedOrder.status || 'Pending').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </span>
                        )},
                      ].map((row, i, arr) => (
                        <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                          <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{row.label}</td>
                          <td style={{ padding: '9px 14px', color: '#1e293b', lineHeight: '1.4' }}>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing & Ad Attribution */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #8b5cf6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  🎯 Marketing & Ad Attribution
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <tbody>
                      {[
                        { 
                          label: 'Traffic Source', 
                          value: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {renderTrafficBadge(selectedOrder)}
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                ({selectedOrder.traffic_source_display || selectedOrder.traffic_source || 'Organic'})
                              </span>
                            </div>
                          )
                        },
                        { label: 'Campaign (utm_campaign)', value: selectedOrder.utm_campaign || '—' },
                        { label: 'Ad Content (utm_content)', value: selectedOrder.utm_content || '—' },
                        { label: 'Medium (utm_medium)', value: selectedOrder.utm_medium || '—' },
                        { label: 'Keywords (utm_term)', value: selectedOrder.utm_term || '—' },
                        { label: 'Click ID (fbclid / ttclid)', value: selectedOrder.click_id ? <code style={{ fontSize: '11px', wordBreak: 'break-all', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{selectedOrder.click_id}</code> : '—' },
                        { label: 'Landing Page', value: selectedOrder.landing_page ? <span style={{ fontSize: '11px', wordBreak: 'break-all', color: '#2563eb' }}>{selectedOrder.landing_page}</span> : '—' },
                        { label: 'Referrer URL', value: selectedOrder.referrer_url ? <span style={{ fontSize: '11px', wordBreak: 'break-all', color: '#64748b' }}>{selectedOrder.referrer_url}</span> : '—' },
                      ].map((row, i, arr) => (
                        <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                          <td style={{ padding: '8px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{row.label}</td>
                          <td style={{ padding: '8px 14px', color: '#1e293b', lineHeight: '1.4' }}>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Order Items
                </h3>
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>#</th>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Product</th>
                        <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Variants</th>
                        <th style={{ padding: '9px 14px', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Qty</th>
                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Unit Price</th>
                        <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((item: any, idx: number) => {
                        const color = item.selected_color || item.color || '';
                        const size = item.selected_size || item.size || '';
                        const weight = item.selected_weight || item.weight || '';
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                            <td style={{ padding: '9px 14px', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>
                            <td style={{ padding: '9px 14px', color: '#1e293b', fontWeight: '500' }}>{item.product_name || item.name || 'Unknown Product'}</td>
                            <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '0.8rem' }}>
                              {(color || size || weight) ? (
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  {color && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                      {color}
                                    </span>
                                  )}
                                  {size && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      {size}
                                    </span>
                                  )}
                                  {weight && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                      {weight}
                                    </span>
                                  )}
                                </div>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '9px 14px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>{item.quantity}</td>
                            <td style={{ padding: '9px 14px', textAlign: 'right', color: '#64748b' }}>
                              <div>৳{parseFloat(item.price || 0).toFixed(2)}</div>
                              {parseFloat(item.buying_price || 0) > 0 && (
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                  Buy: ৳{parseFloat(item.buying_price).toFixed(2)}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '9px 14px', textAlign: 'right', color: '#2563eb', fontWeight: '600' }}>
                              <div>৳{parseFloat(item.subtotal || 0).toFixed(2)}</div>
                              {item.item_profit !== undefined && item.item_profit !== null && (
                                <div style={{ fontSize: '0.7rem', color: parseFloat(item.item_profit) >= 0 ? '#16a34a' : '#ef4444', fontWeight: '600' }}>
                                  {parseFloat(item.item_profit) >= 0 ? '+' : ''}৳{parseFloat(item.item_profit).toFixed(2)} profit
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment Summary */}
              <div>
                <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                  Payment Summary
                </h3>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', maxWidth: '380px', marginLeft: 'auto' }}>
                  {(() => {
                    const items = selectedOrder.items || [];
                    const itemsSubtotal = items.length > 0
                      ? items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || (parseFloat(i.price || 0) * (i.quantity || 1)) || 0), 0)
                      : Math.max(0, (parseFloat(selectedOrder.total_amount || 0) - parseFloat(selectedOrder.shipping_cost || 0)));
                    const shipping = parseFloat(selectedOrder.shipping_cost) || 0;
                    const discount = parseFloat(selectedOrder.discount_amount) || 0;
                    const total = itemsSubtotal + shipping - discount;
                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                          <span>Items Subtotal</span><span style={{ fontWeight: '500' }}>৳{itemsSubtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                          <span>Shipping Charge</span><span style={{ fontWeight: '500' }}>৳{shipping.toFixed(2)}</span>
                        </div>
                        {discount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#16a34a', marginBottom: '7px' }}>
                            <span>Discount {selectedOrder.coupon ? `(Coupon)` : selectedOrder.admin_discount_type ? `(Admin ${selectedOrder.admin_discount_type === 'percentage' ? selectedOrder.admin_discount_value + '%' : ''})` : ''}</span>
                            <span style={{ fontWeight: '600' }}>−৳{discount.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '10px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                          <span>Grand Total (COD)</span>
                          <span style={{ color: '#2563eb' }}>৳{total.toFixed(2)}</span>
                        </div>
                        {selectedOrder.gross_profit !== undefined && selectedOrder.gross_profit !== null && (
                          <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '10px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.84rem', color: '#0f766e', fontWeight: '600' }}>💰 Gross Profit (অর্ডার লাভ)</span>
                            <span style={{ 
                              color: parseFloat(selectedOrder.gross_profit || 0) >= 0 ? '#059669' : '#dc2626', 
                              fontWeight: '700', 
                              fontSize: '0.9rem',
                              background: parseFloat(selectedOrder.gross_profit || 0) >= 0 ? '#ecfdf5' : '#fef2f2',
                              border: `1px solid ${parseFloat(selectedOrder.gross_profit || 0) >= 0 ? '#a7f3d0' : '#fecaca'}`,
                              padding: '2px 8px',
                              borderRadius: '6px'
                            }}>
                              {parseFloat(selectedOrder.gross_profit || 0) >= 0 ? '+' : ''}৳{parseFloat(selectedOrder.gross_profit || 0).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Steadfast Courier Info */}
              {selectedOrder.steadfast_consignment_id && (
                <div>
                  <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                    Courier / Shipping
                  </h3>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                      <tbody>
                        {[
                          { label: 'Consignment ID', value: selectedOrder.steadfast_consignment_id },
                          { label: 'Tracking Code', value: <span style={{ color: '#2563eb', fontWeight: '600' }}>{selectedOrder.steadfast_tracking_code}</span> },
                          { label: 'Courier Status', value: <span style={{ padding: '3px 10px', borderRadius: '5px', backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.75rem', fontWeight: '600' }}>{selectedOrder.steadfast_status || 'In Transit'}</span> },
                          ...(selectedOrder.steadfast_sent_at ? [{ label: 'Sent to Courier', value: new Date(selectedOrder.steadfast_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }] : []),
                        ].map((row, i, arr) => (
                          <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                            <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0' }}>{row.label}</td>
                            <td style={{ padding: '9px 14px', color: '#1e293b' }}>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                    Order Notes
                  </h3>
                  <p style={{ padding: '12px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontSize: '0.84rem', margin: 0, lineHeight: '1.5' }}>
                    {selectedOrder.notes}
                  </p>
                </div>
              )}
            </div>


            {/* Modal Footer - Fixed */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8fafc',
              borderRadius: '0 0 12px 12px',
              flexShrink: 0
            }}>
              <button
                onClick={() => handleDeleteOrder(selectedOrder.id)}
                style={{
                  padding: '9px 16px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fecaca'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
              >
                <Trash2 size={14} /> Delete Order
              </button>
              <button 
                onClick={closeViewModal}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk Send to Steadfast Result Modal ─── */}
      {bulkSendResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '14px',
            width: '100%', maxWidth: '680px', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: bulkSendResult.failed === 0
                ? 'linear-gradient(135deg, #059669, #065f46)'
                : bulkSendResult.sent === 0 && bulkSendResult.already_sent === 0
                ? 'linear-gradient(135deg, #dc2626, #991b1b)'
                : 'linear-gradient(135deg, #d97706, #92400e)',
              color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>📦</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Send to Steadfast — Results</h2>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                    ✅ {bulkSendResult.sent} sent
                    {bulkSendResult.already_sent > 0 && <> &nbsp;·&nbsp; 🔵 {bulkSendResult.already_sent} already sent</>}
                    {bulkSendResult.failed > 0 && <> &nbsp;·&nbsp; ❌ {bulkSendResult.failed} failed</>}
                  </p>
                </div>
              </div>
              <button onClick={() => setBulkSendResult(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: '8px', color: 'white', fontSize: '20px',
                width: '36px', height: '36px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>×</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order #</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Tracking Code</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkSendResult.results.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_sent ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.already_sent
                          ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔵 Already Sent</span>
                          : r.success
                          ? <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Sent</span>
                          : <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                        }
                      </td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.consignment_id ? '#1d4ed8' : '#9ca3af', fontWeight: r.consignment_id ? '700' : '400' }}>
                        {r.consignment_id || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: r.tracking_code ? '#1d4ed8' : '#9ca3af' }}>
                        {r.tracking_code || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', color: r.success ? '#6b7280' : '#dc2626', fontSize: '12px' }}>
                        {r.message || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setBulkSendResult(null)} style={{
                padding: '8px 22px', background: '#111827', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer'
              }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Pickup Request Result Modal (Manifest) ─── */}
      {pickupResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, backdropFilter: 'blur(4px)', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '14px',
            width: '100%', maxWidth: '720px', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 24px',
              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
              color: 'white',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>🚚</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Pickup Request — Manifest</h2>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                    ✅ {pickupResult.requested} requested
                    {(pickupResult as any).previously_requested > 0 && <> &nbsp;·&nbsp; 🔄 {(pickupResult as any).previously_requested} re-requested</>}
                    &nbsp;·&nbsp; Total COD: ৳{pickupResult.total_cod.toLocaleString()}
                  </p>
                </div>
              </div>
              <button onClick={() => setPickupResult(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: '8px', color: 'white', fontSize: '20px',
                width: '36px', height: '36px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>×</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              {/* Info banner: pickup already pending */}
              {(pickupResult as any).pickup_already_pending && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>ℹ️</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '13px', marginBottom: '4px' }}>Pickup Already Scheduled</div>
                    <div style={{ color: '#1d4ed8', fontSize: '12px', lineHeight: '1.5' }}>A pending pickup request already exists for your address. These orders have been marked and will be included in the existing pickup.</div>
                  </div>
                </div>
              )}
              {/* API error banner */}
              {pickupResult.pickup_api_error && !(pickupResult as any).pickup_already_pending && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>❌</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '13px', marginBottom: '4px' }}>Pickup Request Failed</div>
                    <div style={{ color: '#b91c1c', fontSize: '12px', lineHeight: '1.5' }}>{pickupResult.pickup_api_error}</div>
                  </div>
                </div>
              )}
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>{pickupResult.requested + pickupResult.already_requested}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Total Parcels</div>
                </div>
                <div style={{ flex: 1, background: '#ede9fe', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#5b21b6' }}>৳{pickupResult.total_cod.toLocaleString()}</div>
                  <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '600' }}>Total COD</div>
                </div>
                <div style={{ flex: 1, background: pickupResult.pickup_api_error ? '#fef2f2' : '#d1fae5', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: pickupResult.pickup_api_error ? '#991b1b' : '#065f46' }}>{pickupResult.requested}</div>
                  <div style={{ fontSize: '11px', color: pickupResult.pickup_api_error ? '#b91c1c' : '#059669', fontWeight: '600' }}>Newly Requested</div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Customer</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                    <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>COD</th>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pickupResult.results.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_requested ? '#f5f3ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '9px 12px', color: '#6b7280', fontSize: '12px' }}>{i + 1}</td>
                      <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                      <td style={{ padding: '9px 12px', color: '#374151' }}>{r.customer_name || '—'}</td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#1d4ed8', fontWeight: '700' }}>
                        {r.consignment_id || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                        ৳{r.cod_amount?.toLocaleString() || '—'}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.pickup_failed
                          ? <span style={{ background: '#fecaca', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                          : r.pickup_already_pending
                            ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>ℹ️ Pending</span>
                            : r.re_requested
                              ? <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔄 Re-requested</span>
                              : <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Requested</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setPickupResult(null)} style={{
                padding: '8px 22px', background: '#111827', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', cursor: 'pointer'
              }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Steadfast status badge colors (used in edit modal tracking section) ──
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function steadfastStatusStyle(status: string | null | undefined): { bg: string; text: string; icon: string } {
  switch (status?.toLowerCase()) {
    case 'in_review':
      return { bg: '#fef3c7', text: '#92400e', icon: '⏳' };
    case 'pending':
      return { bg: '#fef9c3', text: '#854d0e', icon: '🕐' };
    case 'in_transit':
      return { bg: '#dbeafe', text: '#1e40af', icon: '🚚' };
    case 'delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '✅' };
    case 'partial_delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '📦' };
    case 'cancelled':
      return { bg: '#fee2e2', text: '#991b1b', icon: '❌' };
    case 'hold':
      return { bg: '#fce7f3', text: '#9d174d', icon: '⏸️' };
    case 'delivered_approval_pending':
    case 'partial_delivered_approval_pending':
    case 'cancelled_approval_pending':
      return { bg: '#ede9fe', text: '#5b21b6', icon: '⏳' };
    case 'unknown':
      return { bg: '#f3f4f6', text: '#6b7280', icon: '❓' };
    default:
      return { bg: '#f3f4f6', text: '#6b7280', icon: '📋' };
  }
}

export default OrderManagement;
